import { CountryBlocMembershipCode } from './countryBlocMembershipCode';
import { CountryISO2 } from './countryISO2';
import { ITALIAN_REGIONS } from './italianRegions';
import { POLISH_REGIONS } from './polishRegions';
import { SPAIN_REGIONS } from './spainRegions';

enum Locale {
    CS = 'cs',
    DE = 'de',
    EN = 'en',
    ES = 'es',
    FR = 'fr',
    IT = 'it',
    NL = 'nl',
    PL = 'pl',
    PT = 'pt',
    RO = 'ro',
    RU = 'ru',
}

export type CountryConfig = Readonly<{
    i18nNames: Record<Locale, string>;
    rootIddCode: string;
    blocMemberships?: readonly [CountryBlocMembershipCode, ...CountryBlocMembershipCode[]];
    regions?: readonly [Region, ...Region[]];
    taxIdMandatory?: boolean;
    taxIdPattern?: string;
    taxIdExample?: string;
}>;

export type Region = Readonly<{
    name: string;
    code: string;
    provinces?: readonly [Province, ...Province[]];
}>;

export type Province = Readonly<{
    name: string;
    code: string;
}>;

export const countriesConfig = {
    AD: {
        i18nNames: {
            en: 'Andorra',
            it: 'Andorra',
            es: 'Andorra',
            de: 'Andorra',
            pl: 'Andora',
            fr: 'Andorre',
            nl: 'Andorra',
            pt: 'Andorra',
            cs: 'Andorra',
            ro: 'Andorra',
            ru: 'Андорра',
        },
        rootIddCode: '+376',
        taxIdMandatory: true,
        taxIdPattern: '^[A-Z]-\\d{6}-[A-Z]$',
        taxIdExample: 'X-123456-Y',
    },
    AE: {
        i18nNames: {
            en: 'United Arab Emirates',
            es: 'Emiratos Árabes Unidos',
            it: 'Emirati Arabi Uniti',
            de: 'Vereinigte Arabische Emirate',
            pl: 'Zjednoczone Emiraty Arabskie',
            fr: 'Émirats arabes unis',
            nl: 'Verenigde Arabische Emiraten',
            pt: 'Emirados Árabes Unidos',
            cs: 'Spojené arabské emiráty',
            ro: 'Emiratele Arabe Unite',
            ru: 'Объединённые Арабские Эмираты',
        },
        rootIddCode: '+971',
        taxIdMandatory: false,
        taxIdPattern: '^\\d{15}$',
        taxIdExample: '123456789012345',
    },
    AF: {
        i18nNames: {
            en: 'Afghanistan',
            it: 'Afghanistan',
            es: 'Afganistán',
            de: 'Afghanistan',
            pl: 'Afganistan',
            fr: 'Afghanistan',
            nl: 'Afghanistan',
            pt: 'Afeganistão',
            cs: 'Afghánistán',
            ro: 'Afganistan',
            ru: 'Афганистан',
        },
        rootIddCode: '+93',
    },
    AG: {
        i18nNames: {
            en: 'Antigua and Barbuda',
            es: 'Antigua y Barbuda',
            it: 'Antigua e Barbuda',
            de: 'Antigua und Barbuda',
            pl: 'Antigua i Barbuda',
            fr: 'Antigua-et-Barbuda',
            nl: 'Antigua en Barbuda',
            pt: 'Antígua e Barbuda',
            cs: 'Antigua a Barbuda',
            ro: 'Antigua și Barbuda',
            ru: 'Антигуа и Барбуда',
        },
        rootIddCode: '+1268',
    },
    AI: {
        i18nNames: {
            en: 'Anguilla',
            it: 'Anguilla',
            es: 'Anguila',
            de: 'Anguilla',
            pl: 'Anguilla',
            fr: 'Anguilla',
            nl: 'Anguilla',
            pt: 'Anguila',
            cs: 'Anguilla',
            ro: 'Anguilla',
            ru: 'Ангилья',
        },
        rootIddCode: '+1264',
    },
    AL: {
        i18nNames: {
            en: 'Albania',
            it: 'Albania',
            es: 'Albania',
            de: 'Albanien',
            pl: 'Albania',
            fr: 'Albanie',
            nl: 'Albanië',
            pt: 'Albânia',
            cs: 'Albánie',
            ro: 'Albania',
            ru: 'Албания',
        },
        rootIddCode: '+355',
        taxIdMandatory: true,
        taxIdPattern: '^[JKLM]\\d{8}[A-Z]$',
        taxIdExample: 'J12345678K',
    },
    AM: {
        i18nNames: {
            en: 'Armenia',
            it: 'Armenia',
            es: 'Armenia',
            de: 'Armenien',
            pl: 'Armenia',
            fr: 'Arménie',
            nl: 'Armenië',
            pt: 'Arménia',
            cs: 'Arménie',
            ro: 'Armenia',
            ru: 'Армения',
        },
        rootIddCode: '+374',
    },
    AO: {
        i18nNames: {
            en: 'Angola',
            it: 'Angola',
            es: 'Angola',
            de: 'Angola',
            pl: 'Angola',
            fr: 'Angola',
            nl: 'Angola',
            pt: 'Angola',
            cs: 'Angola',
            ro: 'Angola',
            ru: 'Ангола',
        },
        rootIddCode: '+244',
    },
    AQ: {
        i18nNames: {
            en: 'Antarctica',
            it: 'Antartide',
            es: 'Antártida',
            de: 'Antarktis',
            pl: 'Antarktyka',
            fr: 'Antarctique',
            nl: 'Antarctica',
            pt: 'Antártica',
            cs: 'Antarktida',
            ro: 'Antarctica',
            ru: 'Антарктида',
        },
        rootIddCode: 'undefined',
    },
    AR: {
        i18nNames: {
            en: 'Argentina',
            it: 'Argentina',
            es: 'Argentina',
            de: 'Argentinien',
            pl: 'Argentyna',
            fr: 'Argentine',
            nl: 'Argentinië',
            pt: 'Argentina',
            cs: 'Argentina',
            ro: 'Argentina',
            ru: 'Аргентина',
        },
        rootIddCode: '+54',
    },
    AS: {
        i18nNames: {
            en: 'American Samoa',
            it: 'Samoa Americane',
            es: 'Samoa Americana',
            de: 'Amerikanisch-Samoa',
            pl: 'Samoa Amerykańskie',
            fr: 'Samoa américaines',
            nl: 'Amerikaans-Samoa',
            pt: 'Samoa Americana',
            cs: 'Americká Samoa',
            ro: 'Samoa Americană',
            ru: 'Американское Самоа',
        },
        rootIddCode: '+1684',
    },
    AT: {
        i18nNames: {
            en: 'Austria',
            it: 'Austria',
            es: 'Austria',
            de: 'Österreich',
            pl: 'Austria',
            fr: 'Autriche',
            nl: 'Oostenrijk',
            pt: 'Áustria',
            cs: 'Rakousko',
            ro: 'Austria',
            ru: 'Австрия',
        },
        rootIddCode: '+43',
        blocMemberships: ['EU'],
        taxIdMandatory: false,
        taxIdPattern: '^ATU\\d{8}$',
        taxIdExample: 'ATU12345678',
    },
    AU: {
        i18nNames: {
            en: 'Australia',
            it: 'Australia',
            es: 'Australia',
            de: 'Australien',
            pl: 'Australia',
            fr: 'Australie',
            nl: 'Australië',
            pt: 'Austrália',
            cs: 'Austrálie',
            ro: 'Australia',
            ru: 'Австралия',
        },
        rootIddCode: '+61',
    },
    AW: {
        i18nNames: {
            en: 'Aruba',
            it: 'Aruba',
            es: 'Aruba',
            de: 'Aruba',
            pl: 'Aruba',
            fr: 'Aruba',
            nl: 'Aruba',
            pt: 'Aruba',
            cs: 'Aruba',
            ro: 'Aruba',
            ru: 'Аруба',
        },
        rootIddCode: '+297',
    },
    AX: {
        i18nNames: {
            en: 'Åland Islands',
            it: 'Isole Aland',
            es: 'Islas Åland',
            de: 'Ålandinseln',
            pl: 'Wyspy Alandzkie',
            fr: 'Îles Åland',
            nl: 'Ålandeilanden',
            pt: 'Ilhas Åland',
            cs: 'Ålandské ostrovy',
            ro: 'Insulele Åland',
            ru: 'Аландские острова',
        },
        rootIddCode: '+35818',
        blocMemberships: ['EU'],
    },
    AZ: {
        i18nNames: {
            en: 'Azerbaijan',
            it: 'Azerbaijan',
            es: 'Azerbaiyán',
            de: 'Aserbaidschan',
            pl: 'Azerbejdżan',
            fr: 'Azerbaïdjan',
            nl: 'Azerbeidzjan',
            pt: 'Azerbaijão',
            cs: 'Ázerbájdžán',
            ro: 'Azerbaidjan',
            ru: 'Азербайджан',
        },
        rootIddCode: '+994',
    },
    BB: {
        i18nNames: {
            en: 'Barbados',
            it: 'Barbados',
            es: 'Barbados',
            de: 'Barbados',
            pl: 'Barbados',
            fr: 'Barbade',
            nl: 'Barbados',
            pt: 'Barbados',
            cs: 'Barbados',
            ro: 'Barbados',
            ru: 'Барбадос',
        },
        rootIddCode: '+1246',
    },
    BD: {
        i18nNames: {
            en: 'Bangladesh',
            it: 'Bangladesh',
            es: 'Bangladés',
            de: 'Bangladesch',
            pl: 'Bangladesz',
            fr: 'Bangladesh',
            nl: 'Bangladesh',
            pt: 'Bangladesh',
            cs: 'Bangladéš',
            ro: 'Bangladesh',
            ru: 'Бангладеш',
        },
        rootIddCode: '+880',
    },
    BE: {
        i18nNames: {
            en: 'Belgium',
            it: 'Belgio',
            es: 'Bélgica',
            de: 'Belgien',
            pl: 'Belgia',
            fr: 'Belgique',
            nl: 'België',
            pt: 'Bélgica',
            cs: 'Belgie',
            ro: 'Belgia',
            ru: 'Бельгия',
        },
        rootIddCode: '+32',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^BE\\d{10}$',
        taxIdExample: 'BE0123456789',
    },
    BF: {
        i18nNames: {
            en: 'Burkina Faso',
            it: 'Burkina Faso',
            es: 'Burkina Faso',
            de: 'Burkina Faso',
            pl: 'Burkina Faso',
            fr: 'Burkina Faso',
            nl: 'Burkina Faso',
            pt: 'Burquina Faso',
            cs: 'Burkina Faso',
            ro: 'Burkina Faso',
            ru: 'Буркина-Фасо',
        },
        rootIddCode: '+226',
    },
    BG: {
        i18nNames: {
            en: 'Bulgaria',
            it: 'Bulgaria',
            es: 'Bulgaria',
            de: 'Bulgarien',
            pl: 'Bułgaria',
            fr: 'Bulgarie',
            nl: 'Bulgarije',
            pt: 'Bulgária',
            cs: 'Bulharsko',
            ro: 'Bulgaria',
            ru: 'Болгария',
        },
        rootIddCode: '+359',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^BG(\\d{9}|\\d{10})$',
        taxIdExample: 'BG123456789',
    },
    BH: {
        i18nNames: {
            en: 'Bahrain',
            it: 'Bahrein',
            es: 'Baréin',
            de: 'Bahrain',
            pl: 'Bahrajn',
            fr: 'Bahreïn',
            nl: 'Bahrein',
            pt: 'Bahrein',
            cs: 'Bahrajn',
            ro: 'Bahrein',
            ru: 'Бахрейн',
        },
        rootIddCode: '+973',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{15}$',
        taxIdExample: '123456789012345',
    },
    BI: {
        i18nNames: {
            en: 'Burundi',
            it: 'Burundi',
            es: 'Burundi',
            de: 'Burundi',
            pl: 'Burundi',
            fr: 'Burundi',
            nl: 'Burundi',
            pt: 'Burundi',
            cs: 'Burundi',
            ro: 'Burundi',
            ru: 'Бурунди',
        },
        rootIddCode: '+257',
    },
    BJ: {
        i18nNames: {
            en: 'Benin',
            it: 'Benin',
            es: 'Benín',
            de: 'Benin',
            pl: 'Benin',
            fr: 'Bénin',
            nl: 'Benin',
            pt: 'Benim',
            cs: 'Benin',
            ro: 'Benin',
            ru: 'Бенин',
        },
        rootIddCode: '+229',
    },
    BL: {
        i18nNames: {
            en: 'Saint Barthélemy',
            es: 'San Bartolomé',
            it: 'Antille Francesi',
            de: 'Saint-Barthélemy',
            pl: 'Saint-Barthélemy',
            fr: 'Saint-Barthélemy',
            nl: 'Saint-Barthélemy',
            pt: 'São Bartolomeu',
            cs: 'Svatý Bartoloměj',
            ro: 'Sfântul Bartolomeu',
            ru: 'Сен-Бартелеми',
        },
        rootIddCode: '+590',
    },
    BM: {
        i18nNames: {
            en: 'Bermuda',
            it: 'Bermuda',
            es: 'Bermudas',
            de: 'Bermuda',
            pl: 'Bermudy',
            fr: 'Bermudes',
            nl: 'Bermuda',
            pt: 'Bermudas',
            cs: 'Bermudy',
            ro: 'Bermuda',
            ru: 'Бермуды',
        },
        rootIddCode: '+1441',
    },
    BN: {
        i18nNames: {
            en: 'Brunei',
            it: 'Brunei',
            es: 'Brunéi',
            de: 'Brunei',
            pl: 'Brunei',
            fr: 'Brunei',
            nl: 'Brunei',
            pt: 'Brunei',
            cs: 'Brunej',
            ro: 'Brunei',
            ru: 'Бруней',
        },
        rootIddCode: '+673',
    },
    BO: {
        i18nNames: {
            en: 'Bolivia',
            it: 'Bolivia',
            es: 'Bolivia',
            de: 'Bolivien',
            pl: 'Boliwia',
            fr: 'Bolivie',
            nl: 'Bolivia',
            pt: 'Bolívia',
            cs: 'Bolívie',
            ro: 'Bolivia',
            ru: 'Боливия',
        },
        rootIddCode: '+591',
    },
    BQ: {
        i18nNames: {
            en: 'Caribbean Netherlands',
            es: 'Bonaire, San Eustaquio y Saba',
            it: 'Paesi Bassi caraibici',
            de: 'Karibische Niederlande',
            pl: 'Niderlandy Karaibskie',
            fr: 'Pays-Bas caribéens',
            nl: 'Caribisch Nederland',
            pt: 'Países Baixos Caribenhos',
            cs: 'Karibské Nizozemsko',
            ro: 'Antilele Olandeze',
            ru: 'Карибские Нидерланды',
        },
        rootIddCode: '+599',
    },
    BR: {
        i18nNames: {
            en: 'Brazil',
            it: 'Brasile',
            es: 'Brasil',
            de: 'Brasilien',
            pl: 'Brazylia',
            fr: 'Brésil',
            nl: 'Brazilië',
            pt: 'Brasil',
            cs: 'Brazílie',
            ro: 'Brazilia',
            ru: 'Бразилия',
        },
        rootIddCode: '+55',
    },
    BS: {
        i18nNames: {
            en: 'Bahamas',
            it: 'Bahamas',
            es: 'Bahamas',
            de: 'Bahamas',
            pl: 'Bahamy',
            fr: 'Bahamas',
            nl: "Bahama's",
            pt: 'Bahamas',
            cs: 'Bahamy',
            ro: 'Bahamas',
            ru: 'Багамы',
        },
        rootIddCode: '+1242',
    },
    BT: {
        i18nNames: {
            en: 'Bhutan',
            it: 'Bhutan',
            es: 'Bután',
            de: 'Bhutan',
            pl: 'Bhutan',
            fr: 'Bhoutan',
            nl: 'Bhutan',
            pt: 'Butão',
            cs: 'Bhútán',
            ro: 'Bhutan',
            ru: 'Бутан',
        },
        rootIddCode: '+975',
    },
    BV: {
        i18nNames: {
            en: 'Bouvet Island',
            it: 'Isola Bouvet',
            es: 'Isla Bouvet',
            de: 'Bouvetinsel',
            pl: 'Wyspa Bouveta',
            fr: 'Île Bouvet',
            nl: 'Bouveteiland',
            pt: 'Ilha Bouvet',
            cs: 'Bouvetův ostrov',
            ro: 'Insula Bouvet',
            ru: 'Остров Буве',
        },
        rootIddCode: '+47',
    },
    BW: {
        i18nNames: {
            en: 'Botswana',
            it: 'Botswana',
            es: 'Botsuana',
            de: 'Botswana',
            pl: 'Botswana',
            fr: 'Botswana',
            nl: 'Botswana',
            pt: 'Botsuana',
            cs: 'Botswana',
            ro: 'Botswana',
            ru: 'Ботсвана',
        },
        rootIddCode: '+267',
    },
    BY: {
        i18nNames: {
            en: 'Belarus',
            it: 'Bielorussia',
            es: 'Bielorrusia',
            de: 'Weißrussland',
            pl: 'Białoruś',
            fr: 'Biélorussie',
            nl: 'Wit-Rusland',
            pt: 'Bielorrússia',
            cs: 'Bělorusko',
            ro: 'Belarus',
            ru: 'Беларусь',
        },
        rootIddCode: '+375',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{9}$',
        taxIdExample: '123456789',
    },
    BZ: {
        i18nNames: {
            en: 'Belize',
            it: 'Belize',
            es: 'Belice',
            de: 'Belize',
            pl: 'Belize',
            fr: 'Belize',
            nl: 'Belize',
            pt: 'Belize',
            cs: 'Belize',
            ro: 'Belize',
            ru: 'Белиз',
        },
        rootIddCode: '+501',
    },
    CA: {
        i18nNames: {
            en: 'Canada',
            it: 'Canada',
            es: 'Canadá',
            de: 'Kanada',
            pl: 'Kanada',
            fr: 'Canada',
            nl: 'Canada',
            pt: 'Canadá',
            cs: 'Kanada',
            ro: 'Canada',
            ru: 'Канада',
        },
        //quando verrà mappata aggiungere taxIdMandatory: false, in quanto non obbligatorio
        rootIddCode: '+1',
    },
    CC: {
        i18nNames: {
            en: 'Cocos (Keeling) Islands',
            es: 'Islas Cocos',
            it: 'Isole Cocos e Keeling',
            de: 'Kokosinseln',
            pl: 'Wyspy Kokosowe',
            fr: 'Îles Cocos',
            nl: 'Cocoseilanden',
            pt: 'Ilhas Cocos (Keeling)',
            cs: 'Kokosové ostrovy',
            ro: 'Insulele Cocos (Keeling)',
            ru: 'Кокосовые (Килинг) острова',
        },
        rootIddCode: '+61',
    },
    CD: {
        i18nNames: {
            en: 'DR Congo',
            it: 'Congo (Rep. Dem.)',
            es: 'República Democrática del Congo',
            de: 'Demokratische Republik Kongo',
            pl: 'Demokratyczna Republika Konga',
            fr: 'RDC (République Démocratique du Congo)',
            nl: 'Congo (DRC)',
            pt: 'RD Congo',
            cs: 'Demokratická republika Kongo',
            ro: 'Congo (R.D.)',
            ru: 'Демократическая Республика Конго',
        },
        rootIddCode: '+243',
    },
    CF: {
        i18nNames: {
            en: 'Central African Republic',
            es: 'República Centroafricana',
            it: 'Repubblica Centrafricana',
            de: 'Zentralafrikanische Republik',
            pl: 'Republika Środkowoafrykańska',
            fr: 'République centrafricaine',
            nl: 'Centraal-Afrikaanse Republiek',
            pt: 'República Centro-Africana',
            cs: 'Středoafrická republika',
            ro: 'Republica Centrafricană',
            ru: 'Центральноафриканская Республика',
        },
        rootIddCode: '+236',
    },
    CG: {
        i18nNames: {
            en: 'Republic of the Congo',
            it: 'Congo',
            es: 'República del Congo',
            de: 'Republik Kongo',
            pl: 'Kongo',
            fr: 'République du Congo',
            nl: 'Republiek Congo',
            pt: 'República do Congo',
            cs: 'Republika Kongo',
            ro: 'Republica Congo',
            ru: 'Республика Конго',
        },
        rootIddCode: '+242',
    },
    CH: {
        i18nNames: {
            en: 'Switzerland',
            it: 'Svizzera',
            es: 'Suiza',
            de: 'Schweiz',
            pl: 'Szwajcaria',
            fr: 'Suisse',
            nl: 'Zwitserland',
            pt: 'Suíça',
            cs: 'Švýcarsko',
            ro: 'Elveția',
            ru: 'Швейцария',
        },
        rootIddCode: '+41',
        taxIdMandatory: false,
        taxIdPattern: '^CHE\\d{9}(MWST|TVA|IVA)?$',
        taxIdExample: 'CHE123456789MWST',
    },
    CI: {
        i18nNames: {
            en: 'Ivory Coast',
            it: "Costa d'Avorio",
            es: 'Costa de Marfil',
            de: 'Elfenbeinküste',
            pl: 'Wybrzeże Kości Słoniowej',
            fr: "Côte d'Ivoire",
            nl: 'Ivoorkust',
            pt: 'Costa do Marfim',
            cs: 'Pobřeží slonoviny',
            ro: 'Coasta de Fildeș',
            ru: 'Кот-д’Ивуар',
        },
        rootIddCode: '+225',
    },
    CK: {
        i18nNames: {
            en: 'Cook Islands',
            it: 'Isole Cook',
            es: 'Islas Cook',
            de: 'Cookinseln',
            pl: 'Wyspy Cooka',
            fr: 'Îles Cook',
            nl: 'Cookeilanden',
            pt: 'Ilhas Cook',
            cs: 'Cookovy ostrovy',
            ro: 'Insulele Cook',
            ru: 'Острова Кука',
        },
        rootIddCode: '+682',
    },
    CL: {
        i18nNames: {
            en: 'Chile',
            it: 'Cile',
            es: 'Chile',
            de: 'Chile',
            pl: 'Chile',
            fr: 'Chili',
            nl: 'Chili',
            pt: 'Chile',
            cs: 'Chile',
            ro: 'Chile',
            ru: 'Чили',
        },
        rootIddCode: '+56',
    },
    CM: {
        i18nNames: {
            en: 'Cameroon',
            it: 'Camerun',
            es: 'Camerún',
            de: 'Kamerun',
            pl: 'Kamerun',
            fr: 'Cameroun',
            nl: 'Kameroen',
            pt: 'Camarões',
            cs: 'Kamerun',
            ro: 'Camerun',
            ru: 'Камерун',
        },
        rootIddCode: '+237',
    },
    CN: {
        i18nNames: {
            en: 'China',
            it: 'Cina',
            es: 'China',
            de: 'China',
            pl: 'Chiny',
            fr: 'Chine',
            nl: 'China',
            pt: 'China',
            cs: 'Čína',
            ro: 'China',
            ru: 'Китай',
        },
        rootIddCode: '+86',
    },
    CO: {
        i18nNames: {
            en: 'Colombia',
            it: 'Colombia',
            es: 'Colombia',
            de: 'Kolumbien',
            pl: 'Kolumbia',
            fr: 'Colombie',
            nl: 'Colombia',
            pt: 'Colômbia',
            cs: 'Kolumbie',
            ro: 'Columbia',
            ru: 'Колумбия',
        },
        rootIddCode: '+57',
    },
    CR: {
        i18nNames: {
            en: 'Costa Rica',
            it: 'Costa Rica',
            es: 'Costa Rica',
            de: 'Costa Rica',
            pl: 'Kostaryka',
            fr: 'Costa Rica',
            nl: 'Costa Rica',
            pt: 'Costa Rica',
            cs: 'Kostarika',
            ro: 'Costa Rica',
            ru: 'Коста-Рика',
        },
        rootIddCode: '+506',
    },
    CU: {
        i18nNames: {
            en: 'Cuba',
            it: 'Cuba',
            es: 'Cuba',
            de: 'Kuba',
            pl: 'Kuba',
            fr: 'Cuba',
            nl: 'Cuba',
            pt: 'Cuba',
            cs: 'Kuba',
            ro: 'Cuba',
            ru: 'Куба',
        },
        rootIddCode: '+53',
    },
    CV: {
        i18nNames: {
            en: 'Cape Verde',
            it: 'Capo Verde',
            es: 'Cabo Verde',
            de: 'Kap Verde',
            pl: 'Republika Zielonego Przylądka',
            fr: 'Cap-Vert',
            nl: 'Kaapverdië',
            pt: 'Cabo Verde',
            cs: 'Kapverdy',
            ro: 'Capul Verde',
            ru: 'Кабо-Верде',
        },
        rootIddCode: '+238',
    },
    CW: {
        i18nNames: {
            en: 'Curaçao',
            it: 'Curaçao',
            es: 'Curazao',
            de: 'Curaçao',
            pl: 'Curaçao',
            fr: 'Curaçao',
            nl: 'Curaçao',
            pt: 'Curaçau',
            cs: 'Curaçao',
            ro: 'Curaçao',
            ru: 'Кюрасао',
        },
        rootIddCode: '+599',
    },
    CX: {
        i18nNames: {
            en: 'Christmas Island',
            es: 'Isla Christmas',
            it: 'Isola di Natale',
            de: 'Weihnachtsinsel',
            pl: 'Wyspa Bożego Narodzenia',
            fr: 'Île Christmas',
            nl: 'Christmaseiland',
            pt: 'Ilha Christmas',
            cs: 'Vánoční ostrov',
            ro: 'Insula Christmas',
            ru: 'Остров Рождества',
        },
        rootIddCode: '+61',
    },
    CY: {
        i18nNames: {
            en: 'Cyprus',
            it: 'Cipro',
            es: 'Chipre',
            de: 'Zypern',
            pl: 'Cypr',
            fr: 'Chypre',
            nl: 'Cyprus',
            pt: 'Chipre',
            cs: 'Kypr',
            ro: 'Cipru',
            ru: 'Кипр',
        },
        rootIddCode: '+357',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^CY\\d{8}[A-Z]$',
        taxIdExample: 'CY12345678A',
    },
    CZ: {
        i18nNames: {
            en: 'Czechia',
            it: 'Cechia',
            es: 'Chequia',
            de: 'Tschechien',
            pl: 'Czechy',
            fr: 'Tchéquie',
            nl: 'Tsjechië',
            pt: 'Chéquia',
            cs: 'Česko',
            ro: 'Cehia',
            ru: 'Чехия',
        },
        rootIddCode: '+420',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^CZ(\\d{8}|\\d{9}|\\d{10})$',
        taxIdExample: 'CZ12345678',
    },
    DE: {
        i18nNames: {
            en: 'Germany',
            it: 'Germania',
            es: 'Alemania',
            de: 'Deutschland',
            pl: 'Niemcy',
            fr: 'Allemagne',
            nl: 'Duitsland',
            pt: 'Alemanha',
            cs: 'Německo',
            ro: 'Germania',
            ru: 'Германия',
        },
        rootIddCode: '+49',
        blocMemberships: ['EU'],
        taxIdMandatory: false,
        taxIdPattern: '^DE\\d{9}$',
        taxIdExample: 'DE123456789',
    },
    DJ: {
        i18nNames: {
            en: 'Djibouti',
            it: 'Gibuti',
            es: 'Yibuti',
            de: 'Dschibuti',
            pl: 'Dżibuti',
            fr: 'Djibouti',
            nl: 'Djibouti',
            pt: 'Djibuti',
            cs: 'Džibutsko',
            ro: 'Djibouti',
            ru: 'Джибути',
        },
        rootIddCode: '+253',
    },
    DK: {
        i18nNames: {
            en: 'Denmark',
            it: 'Danimarca',
            es: 'Dinamarca',
            de: 'Dänemark',
            pl: 'Dania',
            fr: 'Danemark',
            nl: 'Denemarken',
            pt: 'Dinamarca',
            cs: 'Dánsko',
            ro: 'Danemarca',
            ru: 'Дания',
        },
        rootIddCode: '+45',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^DK\\d{8}$',
        taxIdExample: 'DK12345678',
    },
    DM: {
        i18nNames: {
            en: 'Dominica',
            it: 'Dominica',
            es: 'Dominica',
            de: 'Dominica',
            pl: 'Dominika',
            fr: 'Dominique',
            nl: 'Dominica',
            pt: 'Dominica',
            cs: 'Dominika',
            ro: 'Dominica',
            ru: 'Доминика',
        },
        rootIddCode: '+1767',
    },
    DO: {
        i18nNames: {
            en: 'Dominican Republic',
            es: 'República Dominicana',
            it: 'Repubblica Dominicana',
            de: 'Dominikanische Republik',
            pl: 'Dominikana',
            fr: 'République dominicaine',
            nl: 'Dominicaanse Republiek',
            pt: 'República Dominicana',
            cs: 'Dominikánská republika',
            ro: 'Republica Dominicană',
            ru: 'Доминиканская Республика',
        },
        rootIddCode: '+1849',
    },
    DZ: {
        i18nNames: {
            en: 'Algeria',
            it: 'Algeria',
            es: 'Argelia',
            de: 'Algerien',
            pl: 'Algieria',
            fr: 'Algérie',
            nl: 'Algerije',
            pt: 'Argélia',
            cs: 'Alžírsko',
            ro: 'Algeria',
            ru: 'Алжир',
        },
        rootIddCode: '+213',
        taxIdMandatory: true,
        taxIdPattern: '^(\\d{15}|\\d{20})$',
        taxIdExample: '123456789012345',
    },
    EC: {
        i18nNames: {
            en: 'Ecuador',
            it: 'Ecuador',
            es: 'Ecuador',
            de: 'Ecuador',
            pl: 'Ekwador',
            fr: 'Équateur',
            nl: 'Ecuador',
            pt: 'Equador',
            cs: 'Ekvádor',
            ro: 'Ecuador',
            ru: 'Эквадор',
        },
        rootIddCode: '+593',
    },
    EE: {
        i18nNames: {
            en: 'Estonia',
            it: 'Estonia',
            es: 'Estonia',
            de: 'Estland',
            pl: 'Estonia',
            fr: 'Estonie',
            nl: 'Estland',
            pt: 'Estónia',
            cs: 'Estonsko',
            ro: 'Estonia',
            ru: 'Эстония',
        },
        rootIddCode: '+372',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^EE\\d{9}$',
        taxIdExample: 'EE123456789',
    },
    EG: {
        i18nNames: {
            en: 'Egypt',
            it: 'Egitto',
            es: 'Egipto',
            de: 'Ägypten',
            pl: 'Egipt',
            fr: 'Égypte',
            nl: 'Egypte',
            pt: 'Egito',
            cs: 'Egypt',
            ro: 'Egipt',
            ru: 'Египет',
        },
        rootIddCode: '+20',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{9}$',
        taxIdExample: '123456789',
    },
    EH: {
        i18nNames: {
            en: 'Western Sahara',
            it: 'Sahara Occidentale',
            es: 'Sáhara Occidental',
            de: 'Westsahara',
            pl: 'Sahara Zachodnia',
            fr: 'Sahara occidental',
            nl: 'Westelijke Sahara',
            pt: 'Saara Ocidental',
            cs: 'Západní Sahara',
            ro: 'Sahara Occidentală',
            ru: 'Западная Сахара',
        },
        rootIddCode: '+2125289',
    },
    ER: {
        i18nNames: {
            en: 'Eritrea',
            it: 'Eritrea',
            es: 'Eritrea',
            de: 'Eritrea',
            pl: 'Erytrea',
            fr: 'Érythrée',
            nl: 'Eritrea',
            pt: 'Eritreia',
            cs: 'Eritrea',
            ro: 'Eritreea',
            ru: 'Эритрея',
        },
        rootIddCode: '+291',
    },
    ES: {
        i18nNames: {
            en: 'Spain',
            it: 'Spagna',
            es: 'España',
            de: 'Spanien',
            pl: 'Hiszpania',
            fr: 'Espagne',
            nl: 'Spanje',
            pt: 'Espanha',
            cs: 'Španělsko',
            ro: 'Spania',
            ru: 'Испания',
        },
        rootIddCode: '+34',
        blocMemberships: ['EU'],
        regions: SPAIN_REGIONS,
        taxIdMandatory: true,
        taxIdPattern: '^ES[A-Z0-9]\\d{7}[A-Z0-9]$',
        taxIdExample: 'ESX1234567Y',
    },
    ET: {
        i18nNames: {
            en: 'Ethiopia',
            it: 'Etiopia',
            es: 'Etiopía',
            de: 'Äthiopien',
            pl: 'Etiopia',
            fr: 'Éthiopie',
            nl: 'Ethiopië',
            pt: 'Etiópia',
            cs: 'Etiopie',
            ro: 'Etiopia',
            ru: 'Эфиопия',
        },
        rootIddCode: '+251',
    },
    FI: {
        i18nNames: {
            en: 'Finland',
            it: 'Finlandia',
            es: 'Finlandia',
            de: 'Finnland',
            pl: 'Finlandia',
            fr: 'Finlande',
            nl: 'Finland',
            pt: 'Finlândia',
            cs: 'Finsko',
            ro: 'Finlanda',
            ru: 'Финляндия',
        },
        rootIddCode: '+358',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^FI\\d{8}$',
        taxIdExample: 'FI12345678',
    },
    FJ: {
        i18nNames: {
            en: 'Fiji',
            it: 'Figi',
            es: 'Fiyi',
            de: 'Fidschi',
            pl: 'Fidżi',
            fr: 'Fidji',
            nl: 'Fiji',
            pt: 'Fiji',
            cs: 'Fidži',
            ro: 'Fiji',
            ru: 'Фиджи',
        },
        rootIddCode: '+679',
    },
    FK: {
        i18nNames: {
            en: 'Falkland Islands',
            es: 'Islas Malvinas',
            it: 'Isole Falkland o Isole Malvine',
            de: 'Falklandinseln',
            pl: 'Wyspy Falklandzkie',
            fr: 'Îles Falkland',
            nl: 'Falklandeilanden',
            pt: 'Ilhas Malvinas',
            cs: 'Falklandské ostrovy',
            ro: 'Insulele Falkland',
            ru: 'Фолклендские острова',
        },
        rootIddCode: '+500',
    },
    FM: {
        i18nNames: {
            en: 'Micronesia',
            it: 'Micronesia',
            es: 'Estados Federados de Micronesia',
            de: 'Mikronesien',
            pl: 'Mikronezja',
            fr: 'Micronésie',
            nl: 'Micronesië',
            pt: 'Micronésia',
            cs: 'Mikronésie',
            ro: 'Micronezia',
            ru: 'Микронезия',
        },
        rootIddCode: '+691',
    },
    FO: {
        i18nNames: {
            en: 'Faroe Islands',
            it: 'Isole Far Oer',
            es: 'Islas Feroe',
            de: 'Färöer-Inseln',
            pl: 'Wyspy Owcze',
            fr: 'Îles Féroé',
            nl: 'Faeröer',
            pt: 'Ilhas Faroé',
            cs: 'Faerské ostrovy',
            ro: 'Insulele Feroe',
            ru: 'Фарерские острова',
        },
        rootIddCode: '+298',
    },
    FR: {
        i18nNames: {
            en: 'France',
            it: 'Francia',
            es: 'Francia',
            de: 'Frankreich',
            pl: 'Francja',
            fr: 'France',
            nl: 'Frankrijk',
            pt: 'França',
            cs: 'Francie',
            ro: 'Franța',
            ru: 'Франция',
        },
        rootIddCode: '+33',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^FR[A-Z0-9]{2}\\d{9}$',
        taxIdExample: 'FR1A123456789',
    },
    GA: {
        i18nNames: {
            en: 'Gabon',
            it: 'Gabon',
            es: 'Gabón',
            de: 'Gabun',
            pl: 'Gabon',
            fr: 'Gabon',
            nl: 'Gabon',
            pt: 'Gabão',
            cs: 'Gabon',
            ro: 'Gabon',
            ru: 'Габон',
        },
        rootIddCode: '+241',
    },
    GB: {
        i18nNames: {
            en: 'United Kingdom',
            it: 'Regno Unito',
            es: 'Reino Unido',
            de: 'Vereinigtes Königreich',
            pl: 'Zjednoczone Królestwo',
            fr: 'Royaume-Uni',
            nl: 'Verenigd Koninkrijk',
            pt: 'Reino Unido',
            cs: 'Spojené království',
            ro: 'Regatul Unit',
            ru: 'Великобритания',
        },
        rootIddCode: '+44',
        taxIdMandatory: false,
        taxIdPattern: '^GB(\\d{9}|\\d{12}|GD(0\\d{2}|[1-4]\\d{2})|HA([5-9]\\d{2}))$',
        taxIdExample: 'GB123456789',
    },
    GD: {
        i18nNames: {
            en: 'Grenada',
            it: 'Grenada',
            es: 'Granada',
            de: 'Grenada',
            pl: 'Grenada',
            fr: 'Grenade',
            nl: 'Grenada',
            pt: 'Granada',
            cs: 'Grenada',
            ro: 'Grenada',
            ru: 'Гренада',
        },
        rootIddCode: '+1473',
    },
    GE: {
        i18nNames: {
            en: 'Georgia',
            it: 'Georgia',
            es: 'Georgia',
            de: 'Georgien',
            pl: 'Gruzja',
            fr: 'Géorgie',
            nl: 'Georgië',
            pt: 'Geórgia',
            cs: 'Gruzie',
            ro: 'Georgia',
            ru: 'Грузия',
        },
        rootIddCode: '+995',
    },
    GF: {
        i18nNames: {
            en: 'French Guiana',
            it: 'Guyana francese',
            es: 'Guayana Francesa',
            de: 'Französisch-Guayana',
            pl: 'Gujana Francuska',
            fr: 'Guyane française',
            nl: 'Frans-Guyana',
            pt: 'Guiana Francesa',
            cs: 'Francouzská Guyana',
            ro: 'Guyana Franceză',
            ru: 'Французская Гвиана',
        },
        rootIddCode: '+594',
        blocMemberships: ['EU'],
    },
    GG: {
        i18nNames: {
            en: 'Guernsey',
            it: 'Guernsey',
            es: 'Guernsey',
            de: 'Guernsey',
            pl: 'Guernsey',
            fr: 'Guernesey',
            nl: 'Guernsey',
            pt: 'Guernsey',
            cs: 'Guernsey',
            ro: 'Guernsey',
            ru: 'Гернси',
        },
        rootIddCode: '+44',
    },
    GH: {
        i18nNames: {
            en: 'Ghana',
            it: 'Ghana',
            es: 'Ghana',
            de: 'Ghana',
            pl: 'Ghana',
            fr: 'Ghana',
            nl: 'Ghana',
            pt: 'Gana',
            cs: 'Ghana',
            ro: 'Ghana',
            ru: 'Гана',
        },
        rootIddCode: '+233',
    },
    GI: {
        i18nNames: {
            en: 'Gibraltar',
            it: 'Gibilterra',
            es: 'Gibraltar',
            de: 'Gibraltar',
            pl: 'Gibraltar',
            fr: 'Gibraltar',
            nl: 'Gibraltar',
            pt: 'Gibraltar',
            cs: 'Gibraltar',
            ro: 'Gibraltar',
            ru: 'Гибралтар',
        },
        rootIddCode: '+350',
    },
    GL: {
        i18nNames: {
            en: 'Greenland',
            it: 'Groenlandia',
            es: 'Groenlandia',
            de: 'Grönland',
            pl: 'Grenlandia',
            fr: 'Groenland',
            nl: 'Groenland',
            pt: 'Gronelândia',
            cs: 'Grónsko',
            ro: 'Groenlanda',
            ru: 'Гренландия',
        },
        rootIddCode: '+299',
    },
    GM: {
        i18nNames: {
            en: 'Gambia',
            it: 'Gambia',
            es: 'Gambia',
            de: 'Gambia',
            pl: 'Gambia',
            fr: 'Gambie',
            nl: 'Gambia',
            pt: 'Gâmbia',
            cs: 'Gambie',
            ro: 'Gambia',
            ru: 'Гамбия',
        },
        rootIddCode: '+220',
    },
    GN: {
        i18nNames: {
            en: 'Guinea',
            it: 'Guinea',
            es: 'Guinea',
            de: 'Guinea',
            pl: 'Gwinea',
            fr: 'Guinée',
            nl: 'Guinee',
            pt: 'Guiné',
            cs: 'Guinea',
            ro: 'Guineea',
            ru: 'Гвинея',
        },
        rootIddCode: '+224',
    },
    GP: {
        i18nNames: {
            en: 'Guadeloupe',
            it: 'Guadeloupa',
            es: 'Guadalupe',
            de: 'Guadeloupe',
            pl: 'Gwadelupa',
            fr: 'Guadeloupe',
            nl: 'Guadeloupe',
            pt: 'Guadalupe',
            cs: 'Guadeloupe',
            ro: 'Guadelupa',
            ru: 'Гваделупа',
        },
        rootIddCode: '+590',
    },
    GQ: {
        i18nNames: {
            en: 'Equatorial Guinea',
            es: 'Guinea Ecuatorial',
            it: 'Guinea Equatoriale',
            de: 'Äquatorialguinea',
            pl: 'Gwinea Równikowa',
            fr: 'Guinée équatoriale',
            nl: 'Equatoriaal-Guinea',
            pt: 'Guiné Equatorial',
            cs: 'Rovníková Guinea',
            ro: 'Guineea Ecuatorială',
            ru: 'Экваториальная Гвинея',
        },
        rootIddCode: '+240',
    },
    GR: {
        i18nNames: {
            en: 'Greece',
            it: 'Grecia',
            es: 'Grecia',
            de: 'Griechenland',
            pl: 'Grecja',
            fr: 'Grèce',
            nl: 'Griekenland',
            pt: 'Grécia',
            cs: 'Řecko',
            ro: 'Grecia',
            ru: 'Греция',
        },
        rootIddCode: '+30',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^EL\\d{9}$',
        taxIdExample: 'EL123456789',
    },
    GS: {
        i18nNames: {
            en: 'South Georgia',
            it: 'Georgia del Sud e Isole Sandwich Meridionali',
            es: 'Islas Georgias del Sur y Sandwich del Sur',
            de: 'Südgeorgien',
            pl: 'Georgia Południowa',
            fr: 'Géorgie du Sud et îles Sandwich du Sud',
            nl: 'Zuid-Georgië en Zuidelijke Sandwicheilanden',
            pt: 'Geórgia do Sul e Ilhas Sandwich do Sul',
            cs: 'Jižní Georgie a Jižní Sandwichovy ostrovy',
            ro: 'Georgia de Sud și Insulele Sandwich de Sud',
            ru: 'Южная Георгия и Южные Сандвичевы острова',
        },
        rootIddCode: '+500',
    },
    GT: {
        i18nNames: {
            en: 'Guatemala',
            it: 'Guatemala',
            es: 'Guatemala',
            de: 'Guatemala',
            pl: 'Gwatemala',
            fr: 'Guatemala',
            nl: 'Guatemala',
            pt: 'Guatemala',
            cs: 'Guatemala',
            ro: 'Guatemala',
            ru: 'Гватемала',
        },
        rootIddCode: '+502',
    },
    GU: {
        i18nNames: {
            en: 'Guam',
            it: 'Guam',
            es: 'Guam',
            de: 'Guam',
            pl: 'Guam',
            fr: 'Guam',
            nl: 'Guam',
            pt: 'Guam',
            cs: 'Guam',
            ro: 'Guam',
            ru: 'Гуам',
        },
        rootIddCode: '+1671',
    },
    GW: {
        i18nNames: {
            en: 'Guinea-Bissau',
            it: 'Guinea-Bissau',
            es: 'Guinea-Bisáu',
            de: 'Guinea-Bissau',
            pl: 'Gwinea-Bissau',
            fr: 'Guinée-Bissau',
            nl: 'Guinee-Bissau',
            pt: 'Guiné-Bissau',
            cs: 'Guinea-Bissau',
            ro: 'Guineea-Bissau',
            ru: 'Гвинея-Бисау',
        },
        rootIddCode: '+245',
    },
    GY: {
        i18nNames: {
            en: 'Guyana',
            it: 'Guyana',
            es: 'Guyana',
            de: 'Guyana',
            pl: 'Gujana',
            fr: 'Guyana',
            nl: 'Guyana',
            pt: 'Guiana',
            cs: 'Guyana',
            ro: 'Guyana',
            ru: 'Гайана',
        },
        rootIddCode: '+592',
    },
    HK: {
        i18nNames: {
            en: 'Hong Kong',
            it: 'Hong Kong',
            es: 'Hong Kong',
            de: 'Hongkong',
            pl: 'Hongkong',
            fr: 'Hong Kong',
            nl: 'Hongkong',
            pt: 'Hong Kong',
            cs: 'Hongkong',
            ro: 'Hong Kong',
            ru: 'Гонконг',
        },
        rootIddCode: '+852',
    },
    HM: {
        i18nNames: {
            en: 'Heard Island and McDonald Islands',
            es: 'Islas Heard y McDonald',
            it: 'Isole Heard e McDonald',
            de: 'Heard und McDonaldinseln',
            pl: 'Wyspy Heard i McDonalda',
            fr: 'Îles Heard et McDonald',
            nl: 'Heard en McDonaldeilanden',
            pt: 'Ilhas Heard e McDonald',
            cs: 'Heardův ostrov a McDonaldovy ostrovy',
            ro: 'Insula Heard și Insulele McDonald',
            ru: 'Остров Херд и острова Макдональд',
        },
        rootIddCode: 'undefined',
    },
    HN: {
        i18nNames: {
            en: 'Honduras',
            it: 'Honduras',
            es: 'Honduras',
            de: 'Honduras',
            pl: 'Honduras',
            fr: 'Honduras',
            nl: 'Honduras',
            pt: 'Honduras',
            cs: 'Honduras',
            ro: 'Honduras',
            ru: 'Гондурас',
        },
        rootIddCode: '+504',
    },
    HR: {
        i18nNames: {
            en: 'Croatia',
            it: 'Croazia',
            es: 'Croacia',
            de: 'Kroatien',
            pl: 'Chorwacja',
            fr: 'Croatie',
            nl: 'Kroatië',
            pt: 'Croácia',
            cs: 'Chorvatsko',
            ro: 'Croația',
            ru: 'Хорватия',
        },
        rootIddCode: '+385',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^HR\\d{11}$',
        taxIdExample: 'HR12345678901',
    },
    HT: {
        i18nNames: {
            en: 'Haiti',
            it: 'Haiti',
            es: 'Haití',
            de: 'Haiti',
            pl: 'Haiti',
            fr: 'Haïti',
            nl: 'Haïti',
            pt: 'Haiti',
            cs: 'Haiti',
            ro: 'Haiti',
            ru: 'Гаити',
        },
        rootIddCode: '+509',
    },
    HU: {
        i18nNames: {
            en: 'Hungary',
            it: 'Ungheria',
            es: 'Hungría',
            de: 'Ungarn',
            pl: 'Węgry',
            fr: 'Hongrie',
            nl: 'Hongarije',
            pt: 'Hungria',
            cs: 'Maďarsko',
            ro: 'Ungaria',
            ru: 'Венгрия',
        },
        rootIddCode: '+36',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^HU\\d{8}$',
        taxIdExample: 'HU12345678',
    },
    ID: {
        i18nNames: {
            en: 'Indonesia',
            it: 'Indonesia',
            es: 'Indonesia',
            de: 'Indonesien',
            pl: 'Indonezja',
            fr: 'Indonésie',
            nl: 'Indonesië',
            pt: 'Indonésia',
            cs: 'Indonésie',
            ro: 'Indonezia',
            ru: 'Индонезия',
        },
        rootIddCode: '+62',
    },
    IE: {
        i18nNames: {
            en: 'Ireland',
            it: 'Irlanda',
            es: 'Irlanda',
            de: 'Irland',
            pl: 'Irlandia',
            fr: 'Irlande',
            nl: 'Ierland',
            pt: 'Irlanda',
            cs: 'Irsko',
            ro: 'Irlanda',
            ru: 'Ирландия',
        },
        rootIddCode: '+353',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^IE\\d{7}[A-Z]{1,2}$',
        taxIdExample: 'IE1234567A',
    },
    IL: {
        i18nNames: {
            en: 'Israel',
            it: 'Israele',
            es: 'Israel',
            de: 'Israel',
            pl: 'Израиль',
            fr: 'Israël',
            nl: 'Israël',
            pt: 'Israel',
            cs: 'Izrael',
            ro: 'Israel',
            ru: 'Израиль',
        },
        rootIddCode: '+972',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{9}$',
        taxIdExample: '123456789',
    },
    IM: {
        i18nNames: {
            en: 'Isle of Man',
            it: 'Isola di Man',
            es: 'Isla de Man',
            de: 'Isle of Man',
            pl: 'Wyspa Man',
            fr: 'Île de Man',
            nl: 'Isle of Man',
            pt: 'Ilha de Man',
            cs: 'Ostrov Man',
            ro: 'Insula Man',
            ru: 'Остров Мэн',
        },
        rootIddCode: '+44',
    },
    IN: {
        i18nNames: {
            en: 'India',
            it: 'India',
            es: 'India',
            de: 'Indien',
            pl: 'Indie',
            fr: 'Inde',
            nl: 'India',
            pt: 'Índia',
            cs: 'Indie',
            ro: 'India',
            ru: 'Индия',
        },
        rootIddCode: '+91',
    },
    IO: {
        i18nNames: {
            en: 'British Indian Ocean Territory',
            es: 'Territorio Británico del Océano Índico',
            it: "Territorio britannico dell'oceano indiano",
            de: 'Britisches Territorium im Indischen Ozean',
            pl: 'Brytyjskie Terytorium Oceanu Indyjskiego',
            fr: "Territoire britannique de l'océan Indien",
            nl: 'Brits Indische Oceaanterritorium',
            pt: 'Território Britânico do Oceano Índico',
            cs: 'Britské indickooceánské území',
            ro: 'Teritoriul Britanic din Oceanul Indian',
            ru: 'Британская территория в Индийском океане',
        },
        rootIddCode: '+246',
    },
    IQ: {
        i18nNames: {
            en: 'Iraq',
            it: 'Iraq',
            es: 'Irak',
            de: 'Ирак',
            pl: 'Irak',
            fr: 'Irak',
            nl: 'Irak',
            pt: 'Iraque',
            cs: 'Irák',
            ro: 'Irak',
            ru: 'Ирак',
        },
        rootIddCode: '+964',
    },
    IR: {
        i18nNames: {
            en: 'Iran',
            it: 'Iran',
            es: 'Irán',
            de: 'Iran',
            pl: 'Iran',
            fr: 'Iran',
            nl: 'Iran',
            pt: 'Irã',
            cs: 'Írán',
            ro: 'Iran',
            ru: 'Иран',
        },
        rootIddCode: '+98',
    },
    IS: {
        i18nNames: {
            en: 'Iceland',
            it: 'Islanda',
            es: 'Islandia',
            de: 'Island',
            pl: 'Islandia',
            fr: 'Islande',
            nl: 'IJsland',
            pt: 'Islândia',
            cs: 'Island',
            ro: 'Islanda',
            ru: 'Исландия',
        },
        rootIddCode: '+354',
        taxIdMandatory: true,
        taxIdPattern: '^IS\\d{5,6}$',
        taxIdExample: 'IS123456',
    },
    IT: {
        i18nNames: {
            en: 'Italy',
            it: 'Italia',
            es: 'Italia',
            de: 'Italien',
            pl: 'Włochy',
            fr: 'Italie',
            nl: 'Italie',
            pt: 'Itália',
            cs: 'Itálie',
            ro: 'Italia',
            ru: 'Италия',
        },
        rootIddCode: '+39',
        blocMemberships: ['EU'],
        regions: ITALIAN_REGIONS,
        taxIdMandatory: true,
        taxIdPattern: '^IT\\d{11}$',
        taxIdExample: 'IT12345678901',
    },
    JE: {
        i18nNames: {
            en: 'Jersey',
            it: 'Isola di Jersey',
            es: 'Jersey',
            de: 'Jersey',
            pl: 'Jersey',
            fr: 'Jersey',
            nl: 'Jersey',
            pt: 'Jersey',
            cs: 'Jersey',
            ro: 'Jersey',
            ru: 'Джерси',
        },
        rootIddCode: '+44',
    },
    JM: {
        i18nNames: {
            en: 'Jamaica',
            it: 'Giamaica',
            es: 'Jamaica',
            de: 'Jamaika',
            pl: 'Jamajka',
            fr: 'Jamaïque',
            nl: 'Jamaica',
            pt: 'Jamaica',
            cs: 'Jamajka',
            ro: 'Jamaica',
            ru: 'Ямайка',
        },
        rootIddCode: '+1876',
    },
    JO: {
        i18nNames: {
            en: 'Jordan',
            it: 'Giordania',
            es: 'Jordania',
            de: 'Jordanien',
            pl: 'Jordania',
            fr: 'Jordanie',
            nl: 'Jordanië',
            pt: 'Jordânia',
            cs: 'Jordánsko',
            ro: 'Iordania',
            ru: 'Иордания',
        },
        rootIddCode: '+962',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{7}$',
        taxIdExample: '1234567',
    },
    JP: {
        i18nNames: {
            en: 'Japan',
            it: 'Giappone',
            es: 'Japón',
            de: 'Japan',
            pl: 'Japonia',
            fr: 'Japon',
            nl: 'Japan',
            pt: 'Japão',
            cs: 'Japonsko',
            ro: 'Japonia',
            ru: 'Япония',
        },
        rootIddCode: '+81',
    },
    KE: {
        i18nNames: {
            en: 'Kenya',
            it: 'Kenya',
            es: 'Kenia',
            de: 'Kenia',
            pl: 'Kenia',
            fr: 'Kenya',
            nl: 'Kenia',
            pt: 'Quênia',
            cs: 'Keňa',
            ro: 'Kenya',
            ru: 'Кения',
        },
        rootIddCode: '+254',
    },
    KG: {
        i18nNames: {
            en: 'Kyrgyzstan',
            it: 'Kirghizistan',
            es: 'Kirguistán',
            de: 'Kirgisistan',
            pl: 'Kirgistan',
            fr: 'Kirghizistan',
            nl: 'Kirgizië',
            pt: 'Quirguistão',
            cs: 'Kyrgyzstán',
            ro: 'Kârgâzstan',
            ru: 'Киргизия',
        },
        rootIddCode: '+996',
    },
    KH: {
        i18nNames: {
            en: 'Cambodia',
            it: 'Cambogia',
            es: 'Camboya',
            de: 'Kambodscha',
            pl: 'Kambodża',
            fr: 'Cambodge',
            nl: 'Cambodja',
            pt: 'Camboja',
            cs: 'Kambodža',
            ro: 'Cambodgia',
            ru: 'Камбоджа',
        },
        rootIddCode: '+855',
    },
    KI: {
        i18nNames: {
            en: 'Kiribati',
            it: 'Kiribati',
            es: 'Kiribati',
            de: 'Kiribati',
            pl: 'Kiribati',
            fr: 'Kiribati',
            nl: 'Kiribati',
            pt: 'Kiribati',
            cs: 'Kiribati',
            ro: 'Kiribati',
            ru: 'Кирибати',
        },
        rootIddCode: '+686',
    },
    KM: {
        i18nNames: {
            en: 'Comoros',
            it: 'Comore',
            es: 'Comoras',
            de: 'Komoren',
            pl: 'Komory',
            fr: 'Comores',
            nl: 'Comoren',
            pt: 'Comores',
            cs: 'Komory',
            ro: 'Comore',
            ru: 'Коморы',
        },
        rootIddCode: '+269',
    },
    KN: {
        i18nNames: {
            en: 'Saint Kitts and Nevis',
            es: 'San Cristóbal y Nieves',
            it: 'Saint Kitts e Nevis',
            de: 'St. Kitts und Nevis',
            pl: 'Saint Kitts i Nevis',
            fr: 'Saint-Christophe-et-Niévès',
            nl: 'Saint Kitts en Nevis',
            pt: 'São Cristóvão e Névis',
            cs: 'Svatý Kryštof a Nevis',
            ro: 'Sfântul Kitts și Nevis',
            ru: 'Сент-Китс и Невис',
        },
        rootIddCode: '+1869',
    },
    KP: {
        i18nNames: {
            en: 'North Korea',
            it: 'Corea del Nord',
            es: 'Corea del Norte',
            de: 'Nordkorea',
            pl: 'Korea Północna',
            fr: 'Corée du Nord',
            nl: 'Noord-Korea',
            pt: 'Coreia do Norte',
            cs: 'Severní Korea',
            ro: 'Coreea de Nord',
            ru: 'Северная Корея',
        },
        rootIddCode: '+850',
    },
    KR: {
        i18nNames: {
            en: 'South Korea',
            it: 'Corea del Sud',
            es: 'Corea del Sur',
            de: 'Südkorea',
            pl: 'Korea Południowa',
            fr: 'Corée du Sud',
            nl: 'Zuid-Korea',
            pt: 'Coreia do Sul',
            cs: 'Jižní Korea',
            ro: 'Coreea de Sud',
            ru: 'Южная Корея',
        },
        rootIddCode: '+82',
    },
    KW: {
        i18nNames: {
            en: 'Kuwait',
            it: 'Kuwait',
            es: 'Kuwait',
            de: 'Kuwait',
            pl: 'Kuwejt',
            fr: 'Koweït',
            nl: 'Кувейт',
            pt: 'Kuwait',
            cs: 'Kuvajt',
            ro: 'Kuweit',
            ru: 'Кувейт',
        },
        rootIddCode: '+965',
    },
    KY: {
        i18nNames: {
            en: 'Cayman Islands',
            it: 'Isole Cayman',
            es: 'Islas Caimán',
            de: 'Kaimaninseln',
            pl: 'Kajmany',
            fr: 'Îles Caïmans',
            nl: 'Kaaimaneilanden',
            pt: 'Ilhas Cayman',
            cs: 'Kajmanské ostrovy',
            ro: 'Insulele Cayman',
            ru: 'Каймановы острова',
        },
        rootIddCode: '+1345',
    },
    KZ: {
        i18nNames: {
            en: 'Kazakhstan',
            it: 'Kazakistan',
            es: 'Kazajistán',
            de: 'Kasachstan',
            pl: 'Kazachstan',
            fr: 'Kazakhstan',
            nl: 'Kazachstan',
            pt: 'Cazaquistão',
            cs: 'Kazachstán',
            ro: 'Kazahstan',
            ru: 'Казахстан',
        },
        rootIddCode: '+77',
    },
    LA: {
        i18nNames: {
            en: 'Laos',
            it: 'Laos',
            es: 'Laos',
            de: 'Laos',
            pl: 'Laos',
            fr: 'Laos',
            nl: 'Laos',
            pt: 'Laos',
            cs: 'Laos',
            ro: 'Laos',
            ru: 'Лаос',
        },
        rootIddCode: '+856',
    },
    LB: {
        i18nNames: {
            en: 'Lebanon',
            it: 'Libano',
            es: 'Líbano',
            de: 'Libanon',
            pl: 'Liban',
            fr: 'Liban',
            nl: 'Libanon',
            pt: 'Líbano',
            cs: 'Libanon',
            ro: 'Liban',
            ru: 'Ливан',
        },
        rootIddCode: '+961',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{1,15}(-(601|603|604))?$',
        taxIdExample: '123456789-601',
    },
    LC: {
        i18nNames: {
            en: 'Saint Lucia',
            it: 'Santa Lucia',
            es: 'Santa Lucía',
            de: 'St. Lucia',
            pl: 'Saint Lucia',
            fr: 'Sainte-Lucie',
            nl: 'Saint Lucia',
            pt: 'Santa Lúcia',
            cs: 'Svatá Lucie',
            ro: 'Sfânta Lucia',
            ru: 'Сент-Люсия',
        },
        rootIddCode: '+1758',
    },
    LI: {
        i18nNames: {
            en: 'Liechtenstein',
            it: 'Liechtenstein',
            es: 'Liechtenstein',
            de: 'Liechtenstein',
            pl: 'Liechtenstein',
            fr: 'Liechtenstein',
            nl: 'Liechtenstein',
            pt: 'Liechtenstein',
            cs: 'Lichtenštejnsko',
            ro: 'Liechtenstein',
            ru: 'Лихтенштейн',
        },
        rootIddCode: '+423',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{5}$',
        taxIdExample: '12345',
    },
    LK: {
        i18nNames: {
            en: 'Sri Lanka',
            it: 'Sri Lanka',
            es: 'Sri Lanka',
            de: 'Sri Lanka',
            pl: 'Sri Lanka',
            fr: 'Sri Lanka',
            nl: 'Sri Lanka',
            pt: 'Sri Lanka',
            cs: 'Srí Lanka',
            ro: 'Sri Lanka',
            ru: 'Шри-Ланка',
        },
        rootIddCode: '+94',
    },
    LR: {
        i18nNames: {
            en: 'Liberia',
            it: 'Liberia',
            es: 'Liberia',
            de: 'Liberia',
            pl: 'Liberia',
            fr: 'Libéria',
            nl: 'Liberia',
            pt: 'Libéria',
            cs: 'Libérie',
            ro: 'Liberia',
            ru: 'Либерия',
        },
        rootIddCode: '+231',
    },
    LS: {
        i18nNames: {
            en: 'Lesotho',
            it: 'Lesotho',
            es: 'Lesoto',
            de: 'Lesotho',
            pl: 'Lesotho',
            fr: 'Lesotho',
            nl: 'Lesotho',
            pt: 'Lesoto',
            cs: 'Lesotho',
            ro: 'Lesotho',
            ru: 'Лесото',
        },
        rootIddCode: '+266',
    },
    LT: {
        i18nNames: {
            en: 'Lithuania',
            it: 'Lituania',
            es: 'Lituania',
            de: 'Litauen',
            pl: 'Litwa',
            fr: 'Lituanie',
            nl: 'Litouwen',
            pt: 'Lituânia',
            cs: 'Litva',
            ro: 'Lituania',
            ru: 'Литва',
        },
        rootIddCode: '+370',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^LT(\\d{9}|\\d{12})$',
        taxIdExample: 'LT123456789',
    },
    LU: {
        i18nNames: {
            en: 'Luxembourg',
            it: 'Lussemburgo',
            es: 'Luxemburgo',
            de: 'Luxemburg',
            pl: 'Luksemburg',
            fr: 'Luxembourg',
            nl: 'Luxemburg',
            pt: 'Luxemburgo',
            cs: 'Lucembursko',
            ro: 'Luxemburg',
            ru: 'Люксембург',
        },
        rootIddCode: '+352',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^LU\\d{8}$',
        taxIdExample: 'LU12345678',
    },
    LV: {
        i18nNames: {
            en: 'Latvia',
            it: 'Lettonia',
            es: 'Letonia',
            de: 'Lettland',
            pl: 'Łotwa',
            fr: 'Lettonie',
            nl: 'Letland',
            pt: 'Letónia',
            cs: 'Lotyšsko',
            ro: 'Letonia',
            ru: 'Латвия',
        },
        rootIddCode: '+371',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^LV\\d{11}$',
        taxIdExample: 'LV12345678901',
    },
    LY: {
        i18nNames: {
            en: 'Libya',
            it: 'Libia',
            es: 'Libia',
            de: 'Libyen',
            pl: 'Libia',
            fr: 'Libye',
            nl: 'Libië',
            pt: 'Líbia',
            cs: 'Libye',
            ro: 'Libia',
            ru: 'Ливия',
        },
        rootIddCode: '+218',
    },
    MA: {
        i18nNames: {
            en: 'Morocco',
            it: 'Marocco',
            es: 'Marruecos',
            de: 'Marokko',
            pl: 'Maroko',
            fr: 'Maroc',
            nl: 'Marokko',
            pt: 'Marrocos',
            cs: 'Maroko',
            ro: 'Maroc',
            ru: 'Марокко',
        },
        rootIddCode: '+212',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{8}$',
        taxIdExample: '12345678',
    },
    MC: {
        i18nNames: {
            en: 'Monaco',
            it: 'Principato di Monaco',
            es: 'Mónaco',
            de: 'Monaco',
            pl: 'Monako',
            fr: 'Monaco',
            nl: 'Monaco',
            pt: 'Mônaco',
            cs: 'Monako',
            ro: 'Monaco',
            ru: 'Монако',
        },
        rootIddCode: '+377',
        taxIdMandatory: true,
        taxIdPattern: '^FR[A-Z0-9]{2}\\d{9}$',
        taxIdExample: 'FR9Z123456789',
    },
    MD: {
        i18nNames: {
            en: 'Moldova',
            it: 'Moldavia',
            es: 'Moldavia',
            de: 'Moldawien',
            pl: 'Mołdawia',
            fr: 'Moldavie',
            nl: 'Moldavië',
            pt: 'Moldávia',
            cs: 'Moldavsko',
            ro: 'Moldova',
            ru: 'Молдова',
        },
        rootIddCode: '+373',
        taxIdMandatory: true,
        taxIdPattern: '^MD\\d{7}$',
        taxIdExample: 'MD1234567',
    },
    ME: {
        i18nNames: {
            en: 'Montenegro',
            it: 'Montenegro',
            es: 'Montenegro',
            de: 'Montenegro',
            pl: 'Czarnogóra',
            fr: 'Monténégro',
            nl: 'Montenegro',
            pt: 'Montenegro',
            cs: 'Černá Hora',
            ro: 'Muntenegru',
            ru: 'Черногория',
        },
        rootIddCode: '+382',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{8}$',
        taxIdExample: '12345678',
    },
    MF: {
        i18nNames: {
            en: 'Saint Martin',
            it: 'Saint Martin',
            es: 'San Martín (parte francesa)',
            de: 'Saint-Martin',
            pl: 'Saint-Martin',
            fr: 'Saint-Martin',
            nl: 'Saint Martin',
            pt: 'São Martinho',
            cs: 'Saint-Martin',
            ro: 'Sfântul Martin',
            ru: 'Сен-Мартен',
        },
        rootIddCode: '+590',
    },
    MG: {
        i18nNames: {
            en: 'Madagascar',
            it: 'Madagascar',
            es: 'Madagascar',
            de: 'Madagaskar',
            pl: 'Madagaskar',
            fr: 'Madagascar',
            nl: 'Madagaskar',
            pt: 'Madagáscar',
            cs: 'Madagaskar',
            ro: 'Madagascar',
            ru: 'Мадагаскар',
        },
        rootIddCode: '+261',
    },
    MH: {
        i18nNames: {
            en: 'Marshall Islands',
            es: 'Islas Marshall',
            it: 'Isole Marshall',
            de: 'Marshallinseln',
            pl: 'Wyspy Marshalla',
            fr: 'Îles Marshall',
            nl: 'Marshalleilanden',
            pt: 'Ilhas Marshall',
            cs: 'Marshallovy ostrovy',
            ro: 'Insulele Marshall',
            ru: 'Маршалловы Острова',
        },
        rootIddCode: '+692',
    },
    MK: {
        i18nNames: {
            en: 'North Macedonia',
            it: 'Macedonia del Nord',
            es: 'Macedonia del Norte',
            de: 'Nordmazedonien',
            pl: 'Macedonia Północna',
            fr: 'Macédoine du Nord',
            nl: 'Noord-Macedonië',
            pt: 'Macedônia do Norte',
            cs: 'Severní Makedonie',
            ro: 'Macedonia de Nord',
            ru: 'Северная Македония',
        },
        rootIddCode: '+389',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{13}$',
        taxIdExample: '1234567890123',
    },
    ML: {
        i18nNames: {
            en: 'Mali',
            it: 'Mali',
            es: 'Malí',
            de: 'Mali',
            pl: 'Mali',
            fr: 'Mali',
            nl: 'Mali',
            pt: 'Mali',
            cs: 'Mali',
            ro: 'Mali',
            ru: 'Мали',
        },
        rootIddCode: '+223',
    },
    MM: {
        i18nNames: {
            en: 'Myanmar',
            it: 'Birmania',
            es: 'Birmania',
            de: 'Myanmar',
            pl: 'Mjanma',
            fr: 'Birmanie',
            nl: 'Myanmar',
            pt: 'Mianmar',
            cs: 'Myanmar',
            ro: 'Myanmar',
            ru: 'Мьянма',
        },
        rootIddCode: '+95',
    },
    MN: {
        i18nNames: {
            en: 'Mongolia',
            it: 'Mongolia',
            es: 'Mongolia',
            de: 'Mongolei',
            pl: 'Mongolia',
            fr: 'Mongolie',
            nl: 'Mongolië',
            pt: 'Mongólia',
            cs: 'Mongolsko',
            ro: 'Mongolia',
            ru: 'Монголия',
        },
        rootIddCode: '+976',
    },
    MO: {
        i18nNames: {
            en: 'Macau',
            it: 'Macao',
            es: 'Macao',
            de: 'Macau',
            pl: 'Makau',
            fr: 'Macao',
            nl: 'Macau',
            pt: 'Macau',
            cs: 'Macao',
            ro: 'Macao',
            ru: 'Макао',
        },
        rootIddCode: '+853',
    },
    MP: {
        i18nNames: {
            en: 'Northern Mariana Islands',
            es: 'Islas Marianas del Norte',
            it: 'Isole Marianne Settentrionali',
            de: 'Nördliche Marianen',
            pl: 'Mariany Północne',
            fr: 'Îles Mariannes du Nord',
            nl: 'Noordelijke Marianen',
            pt: 'Ilhas Marianas do Norte',
            cs: 'Severní Mariany',
            ro: 'Insulele Mariane de Nord',
            ru: 'Северные Марианские острова',
        },
        rootIddCode: '+1670',
    },
    MQ: {
        i18nNames: {
            en: 'Martinique',
            it: 'Martinica',
            es: 'Martinica',
            de: 'Martinique',
            pl: 'Martynika',
            fr: 'Martinique',
            nl: 'Martinique',
            pt: 'Martinica',
            cs: 'Martinik',
            ro: 'Martinica',
            ru: 'Мартиника',
        },
        rootIddCode: '+596',
    },
    MR: {
        i18nNames: {
            en: 'Mauritania',
            it: 'Mauritania',
            es: 'Mauritania',
            de: 'Mauretanien',
            pl: 'Mauretania',
            fr: 'Mauritanie',
            nl: 'Mauritanië',
            pt: 'Mauritânia',
            cs: 'Mauritánie',
            ro: 'Mauritania',
            ru: 'Мавритания',
        },
        rootIddCode: '+222',
    },
    MS: {
        i18nNames: {
            en: 'Montserrat',
            it: 'Montserrat',
            es: 'Montserrat',
            de: 'Montserrat',
            pl: 'Montserrat',
            fr: 'Montserrat',
            nl: 'Montserrat',
            pt: 'Montserrat',
            cs: 'Montserrat',
            ro: 'Montserrat',
            ru: 'Монтсеррат',
        },
        rootIddCode: '+1664',
    },
    MT: {
        i18nNames: {
            en: 'Malta',
            it: 'Malta',
            es: 'Malta',
            de: 'Malta',
            pl: 'Malta',
            fr: 'Malte',
            nl: 'Malta',
            pt: 'Malta',
            cs: 'Malta',
            ro: 'Malta',
            ru: 'Мальта',
        },
        rootIddCode: '+356',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^MT\\d{8}$',
        taxIdExample: 'MT12345678',
    },
    MU: {
        i18nNames: {
            en: 'Mauritius',
            it: 'Mauritius',
            es: 'Mauricio',
            de: 'Mauritius',
            pl: 'Mauritius',
            fr: 'Île Maurice',
            nl: 'Mauritius',
            pt: 'Maurício',
            cs: 'Mauricius',
            ro: 'Mauritius',
            ru: 'Маврикий',
        },
        rootIddCode: '+230',
    },
    MV: {
        i18nNames: {
            en: 'Maldives',
            it: 'Maldive',
            es: 'Maldivas',
            de: 'Malediven',
            pl: 'Malediwy',
            fr: 'Maldives',
            nl: 'Maldiven',
            pt: 'Maldivas',
            cs: 'Maledivy',
            ro: 'Maldive',
            ru: 'Мальдивы',
        },
        rootIddCode: '+960',
    },
    MW: {
        i18nNames: {
            en: 'Malawi',
            it: 'Malawi',
            es: 'Malaui',
            de: 'Malawi',
            pl: 'Malawi',
            fr: 'Malawi',
            nl: 'Malawi',
            pt: 'Maláui',
            cs: 'Malawi',
            ro: 'Malawi',
            ru: 'Малави',
        },
        rootIddCode: '+265',
    },
    MX: {
        i18nNames: {
            en: 'Mexico',
            it: 'Messico',
            es: 'México',
            de: 'Mexiko',
            pl: 'Meksyk',
            fr: 'Mexique',
            nl: 'Mexico',
            pt: 'México',
            cs: 'Mexiko',
            ro: 'Mexic',
            ru: 'Мексика',
        },
        rootIddCode: '+52',
    },
    MY: {
        i18nNames: {
            en: 'Malaysia',
            it: 'Malesia',
            es: 'Malasia',
            de: 'Malaysia',
            pl: 'Malezja',
            fr: 'Malaisie',
            nl: 'Maleisië',
            pt: 'Malásia',
            cs: 'Malajsie',
            ro: 'Malaezia',
            ru: 'Малайзия',
        },
        rootIddCode: '+60',
    },
    MZ: {
        i18nNames: {
            en: 'Mozambique',
            it: 'Mozambico',
            es: 'Mozambique',
            de: 'Mosambik',
            pl: 'Mozambik',
            fr: 'Mozambique',
            nl: 'Mozambique',
            pt: 'Moçambique',
            cs: 'Mosambik',
            ro: 'Mozambic',
            ru: 'Мозамбик',
        },
        rootIddCode: '+258',
    },
    NA: {
        i18nNames: {
            en: 'Namibia',
            it: 'Namibia',
            es: 'Namibia',
            de: 'Namibia',
            pl: 'Namibia',
            fr: 'Namibie',
            nl: 'Namibië',
            pt: 'Namíbia',
            cs: 'Namibie',
            ro: 'Namibia',
            ru: 'Намибия',
        },
        rootIddCode: '+264',
    },
    NC: {
        i18nNames: {
            en: 'New Caledonia',
            it: 'Nuova Caledonia',
            es: 'Nueva Caledonia',
            de: 'Neukaledonien',
            pl: 'Nowa Kaledonia',
            fr: 'Nouvelle-Calédonie',
            nl: 'Nieuw-Caledonië',
            pt: 'Nova Caledônia',
            cs: 'Nová Kaledonie',
            ro: 'Noua Caledonie',
            ru: 'Новая Каледония',
        },
        rootIddCode: '+687',
    },
    NE: {
        i18nNames: {
            en: 'Niger',
            it: 'Niger',
            es: 'Níger',
            de: 'Niger',
            pl: 'Niger',
            fr: 'Niger',
            nl: 'Niger',
            pt: 'Níger',
            cs: 'Niger',
            ro: 'Niger',
            ru: 'Нигер',
        },
        rootIddCode: '+227',
    },
    NF: {
        i18nNames: {
            en: 'Norfolk Island',
            it: 'Isola Norfolk',
            es: 'Isla Norfolk',
            de: 'Norfolkinsel',
            pl: 'Wyspa Norfolk',
            fr: 'Île Norfolk',
            nl: 'Norfolkeiland',
            pt: 'Ilha Norfolk',
            cs: 'Ostrov Norfolk',
            ro: 'Insula Norfolk',
            ru: 'Остров Норфолк',
        },
        rootIddCode: '+672',
    },
    NG: {
        i18nNames: {
            en: 'Nigeria',
            it: 'Nigeria',
            es: 'Nigeria',
            de: 'Nigeria',
            pl: 'Nigeria',
            fr: 'Nigeria',
            nl: 'Nigeria',
            pt: 'Nigéria',
            cs: 'Nigérie',
            ro: 'Nigeria',
            ru: 'Нигерия',
        },
        rootIddCode: '+234',
    },
    NI: {
        i18nNames: {
            en: 'Nicaragua',
            it: 'Nicaragua',
            es: 'Nicaragua',
            de: 'Nicaragua',
            pl: 'Nikaragua',
            fr: 'Nicaragua',
            nl: 'Nicaragua',
            pt: 'Nicarágua',
            cs: 'Nikaragua',
            ro: 'Nicaragua',
            ru: 'Никарагуа',
        },
        rootIddCode: '+505',
    },
    NL: {
        i18nNames: {
            en: 'Netherlands',
            it: 'Paesi Bassi',
            es: 'Países Bajos',
            de: 'Niederlande',
            pl: 'Holandia',
            fr: 'Pays-Bas',
            nl: 'Nederland',
            pt: 'Países Baixos',
            cs: 'Nizozemsko',
            ro: 'Țările de Jos',
            ru: 'Нидерланды',
        },
        rootIddCode: '+31',
        blocMemberships: ['EU'],
        taxIdMandatory: false,
        taxIdPattern: '^NL\\d{9}B\\d{2}$',
        taxIdExample: 'NL123456789B01',
    },
    NO: {
        i18nNames: {
            en: 'Norway',
            it: 'Norvegia',
            es: 'Noruega',
            de: 'Norwegen',
            pl: 'Norwegia',
            fr: 'Norvège',
            nl: 'Noorwegen',
            pt: 'Noruega',
            cs: 'Norsko',
            ro: 'Norvegia',
            ru: 'Норвегия',
        },
        rootIddCode: '+47',
        taxIdMandatory: true,
        taxIdPattern: '^NO\\d{9}MVA$',
        taxIdExample: 'NO123456789MVA',
    },
    NP: {
        i18nNames: {
            en: 'Nepal',
            it: 'Nepal',
            es: 'Nepal',
            de: 'Nepal',
            pl: 'Nepal',
            fr: 'Népal',
            nl: 'Nepal',
            pt: 'Nepal',
            cs: 'Nepál',
            ro: 'Nepal',
            ru: 'Непал',
        },
        rootIddCode: '+977',
    },
    NR: {
        i18nNames: {
            en: 'Nauru',
            it: 'Nauru',
            es: 'Nauru',
            de: 'Nauru',
            pl: 'Nauru',
            fr: 'Nauru',
            nl: 'Nauru',
            pt: 'Nauru',
            cs: 'Nauru',
            ro: 'Nauru',
            ru: 'Науру',
        },
        rootIddCode: '+674',
    },
    NU: {
        i18nNames: {
            en: 'Niue',
            it: 'Niue',
            es: 'Niue',
            de: 'Niue',
            pl: 'Niue',
            fr: 'Niue',
            nl: 'Niue',
            pt: 'Niue',
            cs: 'Niue',
            ro: 'Niue',
            ru: 'Ниуэ',
        },
        rootIddCode: '+683',
    },
    NZ: {
        i18nNames: {
            en: 'New Zealand',
            it: 'Nuova Zelanda',
            es: 'Nueva Zelanda',
            de: 'Neuseeland',
            pl: 'Nowa Zelandia',
            fr: 'Nouvelle-Zélande',
            nl: 'Nieuw-Zeeland',
            pt: 'Nova Zelândia',
            cs: 'Nový Zéland',
            ro: 'Noua Zeelandă',
            ru: 'Новая Зеландия',
        },
        rootIddCode: '+64',
    },
    OM: {
        i18nNames: {
            en: 'Oman',
            it: 'Oman',
            es: 'Omán',
            de: 'Oman',
            pl: 'Oman',
            fr: 'Oman',
            nl: 'Oman',
            pt: 'Omã',
            cs: 'Omán',
            ro: 'Oman',
            ru: 'Оман',
        },
        rootIddCode: '+968',
        taxIdMandatory: true,
        taxIdPattern: '^OM\\d{10}$',
        taxIdExample: 'OM1234567890',
    },
    PA: {
        i18nNames: {
            en: 'Panama',
            it: 'Panama',
            es: 'Panamá',
            de: 'Panama',
            pl: 'Panama',
            fr: 'Panama',
            nl: 'Panama',
            pt: 'Panamá',
            cs: 'Panama',
            ro: 'Panama',
            ru: 'Панама',
        },
        rootIddCode: '+507',
    },
    PE: {
        i18nNames: {
            en: 'Peru',
            it: 'Perù',
            es: 'Perú',
            de: 'Peru',
            pl: 'Peru',
            fr: 'Pérou',
            nl: 'Peru',
            pt: 'Peru',
            cs: 'Peru',
            ro: 'Peru',
            ru: 'Перу',
        },
        rootIddCode: '+51',
    },
    PF: {
        i18nNames: {
            en: 'French Polynesia',
            es: 'Polinesia Francesa',
            it: 'Polinesia Francese',
            de: 'Französisch-Polynesien',
            pl: 'Polinezja Francuska',
            fr: 'Polynésie française',
            nl: 'Frans-Polynesië',
            pt: 'Polinésia Francesa',
            cs: 'Francouzská Polynésie',
            ro: 'Polinezia Franceză',
            ru: 'Французская Полинезия',
        },
        rootIddCode: '+689',
    },
    PG: {
        i18nNames: {
            en: 'Papua New Guinea',
            es: 'Papúa Nueva Guinea',
            it: 'Papua Nuova Guinea',
            de: 'Papua-Neuguinea',
            pl: 'Papua-Nowa Gwinea',
            fr: 'Papouasie-Nouvelle-Guinée',
            nl: 'Papoea-Nieuw-Guinea',
            pt: 'Papua-Nova Guiné',
            cs: 'Papua-Nová Guinea',
            ro: 'Papua Noua Guinee',
            ru: 'Папуа — Новая Гвинея',
        },
        rootIddCode: '+675',
    },
    PH: {
        i18nNames: {
            en: 'Philippines',
            it: 'Filippine',
            es: 'Filipinas',
            de: 'Philippinen',
            pl: 'Filipiny',
            fr: 'Philippines',
            nl: 'Filipijnen',
            pt: 'Filipinas',
            cs: 'Filipíny',
            ro: 'Filipine',
            ru: 'Филиппины',
        },
        rootIddCode: '+63',
    },
    PK: {
        i18nNames: {
            en: 'Pakistan',
            it: 'Pakistan',
            es: 'Pakistán',
            de: 'Pakistan',
            pl: 'Pakistan',
            fr: 'Pakistan',
            nl: 'Pakistan',
            pt: 'Paquistão',
            cs: 'Pákistán',
            ro: 'Pakistan',
            ru: 'Пакистан',
        },
        rootIddCode: '+92',
    },
    PL: {
        i18nNames: {
            en: 'Poland',
            it: 'Polonia',
            es: 'Polonia',
            de: 'Polen',
            pl: 'Polska',
            fr: 'Pologne',
            nl: 'Polen',
            pt: 'Polónia',
            cs: 'Polsko',
            ro: 'Polonia',
            ru: 'Польша',
        },
        rootIddCode: '+48',
        blocMemberships: ['EU'],
        regions: POLISH_REGIONS,
        taxIdMandatory: true,
        taxIdPattern: '^PL\\d{10}$',
        taxIdExample: 'PL1234567890',
    },
    PM: {
        i18nNames: {
            en: 'Saint Pierre and Miquelon',
            es: 'San Pedro y Miquelón',
            it: 'Saint-Pierre e Miquelon',
            de: 'Saint-Pierre und Miquelon',
            pl: 'Saint-Pierre i Miquelon',
            fr: 'Saint-Pierre-et-Miquelon',
            nl: 'Saint-Pierre en Miquelon',
            pt: 'Saint Pierre e Miquelon',
            cs: 'Saint Pierre a Miquelon',
            ro: 'Saint Pierre și Miquelon',
            ru: 'Сен-Пьер и Микелон',
        },
        rootIddCode: '+508',
    },
    PN: {
        i18nNames: {
            en: 'Pitcairn Islands',
            es: 'Islas Pitcairn',
            it: 'Isole Pitcairn',
            de: 'Pitcairninseln',
            pl: 'Wyspy Pitcairn',
            fr: 'Îles Pitcairn',
            nl: 'Pitcairneilanden',
            pt: 'Ilhas Pitcairn',
            cs: 'Pitcairnovy ostrovy',
            ro: 'Insulele Pitcairn',
            ru: 'Острова Питкэрн',
        },
        rootIddCode: '+64',
    },
    PR: {
        i18nNames: {
            en: 'Puerto Rico',
            it: 'Porto Rico',
            es: 'Puerto Rico',
            de: 'Puerto Rico',
            pl: 'Portoryko',
            fr: 'Porto Rico',
            nl: 'Puerto Rico',
            pt: 'Porto Rico',
            cs: 'Portoriko',
            ro: 'Puerto Rico',
            ru: 'Пуэрто-Рико',
        },
        rootIddCode: '+1939',
    },
    PS: {
        i18nNames: {
            en: 'Palestine',
            it: 'Palestina',
            es: 'Palestina',
            de: 'Palästina',
            pl: 'Palestyna',
            fr: 'Palestine',
            nl: 'Palestina',
            pt: 'Palestina',
            cs: 'Palestina',
            ro: 'Palestina',
            ru: 'Палестина',
        },
        rootIddCode: '+970',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{9}$',
        taxIdExample: '123456789',
    },
    PT: {
        i18nNames: {
            en: 'Portugal',
            it: 'Portogallo',
            es: 'Portugal',
            de: 'Portugal',
            pl: 'Portugalia',
            fr: 'Portugal',
            nl: 'Portugal',
            pt: 'Portugal',
            cs: 'Portugalsko',
            ro: 'Portugalia',
            ru: 'Португалия',
        },
        rootIddCode: '+351',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^PT\\d{9}$',
        taxIdExample: 'PT123456789',
    },
    PW: {
        i18nNames: {
            en: 'Palau',
            it: 'Palau',
            es: 'Palaos',
            de: 'Palau',
            pl: 'Palau',
            fr: 'Palaos',
            nl: 'Palau',
            pt: 'Palau',
            cs: 'Palau',
            ro: 'Palau',
            ru: 'Палау',
        },
        rootIddCode: '+680',
    },
    PY: {
        i18nNames: {
            en: 'Paraguay',
            it: 'Paraguay',
            es: 'Paraguay',
            de: 'Paraguay',
            pl: 'Paragwaj',
            fr: 'Paraguay',
            nl: 'Paraguay',
            pt: 'Paraguai',
            cs: 'Paraguay',
            ro: 'Paraguay',
            ru: 'Парагвай',
        },
        rootIddCode: '+595',
    },
    QA: {
        i18nNames: {
            en: 'Qatar',
            it: 'Qatar',
            es: 'Catar',
            de: 'Katar',
            pl: 'Katar',
            fr: 'Qatar',
            nl: 'Qatar',
            pt: 'Catar',
            cs: 'Katar',
            ro: 'Qatar',
            ru: 'Катар',
        },
        rootIddCode: '+974',
    },
    RE: {
        i18nNames: {
            en: 'Réunion',
            it: 'Riunione',
            es: 'Reunión',
            de: 'Réunion',
            pl: 'Reunion',
            fr: 'Réunion',
            nl: 'Réunion',
            pt: 'Reunião',
            cs: 'Réunion',
            ro: 'Réunion',
            ru: 'Реюньон',
        },
        rootIddCode: '+262',
    },
    RO: {
        i18nNames: {
            en: 'Romania',
            it: 'Romania',
            es: 'Rumanía',
            de: 'Rumänien',
            pl: 'Rumunia',
            fr: 'Roumanie',
            nl: 'Roemenië',
            pt: 'Romênia',
            cs: 'Rumunsko',
            ro: 'România',
            ru: 'Румыния',
        },
        rootIddCode: '+40',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^RO\\d{2,10}$',
        taxIdExample: 'RO123456',
    },
    RS: {
        i18nNames: {
            en: 'Serbia',
            it: 'Serbia',
            es: 'Serbia',
            de: 'Serbien',
            pl: 'Serbia',
            fr: 'Serbie',
            nl: 'Servië',
            pt: 'Sérvia',
            cs: 'Srbsko',
            ro: 'Serbia',
            ru: 'Сербия',
        },
        rootIddCode: '+381',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{9}$',
        taxIdExample: '123456789',
    },
    RU: {
        i18nNames: {
            en: 'Russia',
            it: 'Russia',
            es: 'Rusia',
            de: 'Russland',
            pl: 'Rosja',
            fr: 'Russie',
            nl: 'Rusland',
            pt: 'Rússia',
            cs: 'Rusko',
            ro: 'Rusia',
            ru: 'Россия',
        },
        rootIddCode: '+79',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{10}$',
        taxIdExample: '1234567890',
    },
    RW: {
        i18nNames: {
            en: 'Rwanda',
            it: 'Ruanda',
            es: 'Ruanda',
            de: 'Ruanda',
            pl: 'Rwanda',
            fr: 'Rwanda',
            nl: 'Rwanda',
            pt: 'Ruanda',
            cs: 'Rwanda',
            ro: 'Rwanda',
            ru: 'Руанда',
        },
        rootIddCode: '+250',
    },
    SA: {
        i18nNames: {
            en: 'Saudi Arabia',
            it: 'Arabia Saudita',
            es: 'Arabia Saudita',
            de: 'Saudi-Arabien',
            pl: 'Arabia Saudyjska',
            fr: 'Arabie Saoudite',
            nl: 'Saoedi-Arabië',
            pt: 'Arábia Saudita',
            cs: 'Saúdská Arábie',
            ro: 'Arabia Saudită',
            ru: 'Саудовская Аравия',
        },
        rootIddCode: '+966',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{15}$',
        taxIdExample: '123456789012345',
    },
    BA: {
        i18nNames: {
            en: 'Bosnia and Herzegovina',
            es: 'Bosnia y Herzegovina',
            it: 'Bosnia ed Erzegovina',
            de: 'Bosnien und Herzegовина',
            pl: 'Bośnia i Hercegowina',
            fr: 'Bosnie-Herzégovine',
            nl: 'Bosnië en Herzegovina',
            pt: 'Bósnia e Herzegovina',
            cs: 'Bosna a Hercegovina',
            ro: 'Bosnia și Herțegovina',
            ru: 'Босния и Герцеговина',
        },
        rootIddCode: '+387',
        taxIdMandatory: true,
        taxIdPattern: '^(\\d{12}|\\d{13})$',
        taxIdExample: '123456789012',
    },
    SB: {
        i18nNames: {
            en: 'Solomon Islands',
            it: 'Isole Salomone',
            es: 'Islas Salomón',
            de: 'Salomonen',
            pl: 'Wyspy Salomona',
            fr: 'Îles Salomon',
            nl: 'Salomonseilanden',
            pt: 'Ilhas Salomão',
            cs: 'Šalamounovy ostrovy',
            ro: 'Insulele Solomon',
            ru: 'Соломоновы Острова',
        },
        rootIddCode: '+677',
    },
    SC: {
        i18nNames: {
            en: 'Seychelles',
            it: 'Seychelles',
            es: 'Seychelles',
            de: 'Seychellen',
            pl: 'Seszele',
            fr: 'Seychelles',
            nl: 'Seychellen',
            pt: 'Seicheles',
            cs: 'Seychely',
            ro: 'Seychelles',
            ru: 'Сейшельские Острова',
        },
        rootIddCode: '+248',
    },
    SD: {
        i18nNames: {
            en: 'Sudan',
            it: 'Sudan',
            es: 'Sudán',
            de: 'Sudan',
            pl: 'Sudan',
            fr: 'Soudan',
            nl: 'Soedan',
            pt: 'Sudão',
            cs: 'Súdán',
            ro: 'Sudan',
            ru: 'Судан',
        },
        rootIddCode: '+249',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{12}$',
        taxIdExample: '123456789012',
    },
    SE: {
        i18nNames: {
            en: 'Sweden',
            it: 'Svezia',
            es: 'Suecia',
            de: 'Schweden',
            pl: 'Szwecja',
            fr: 'Suède',
            nl: 'Zweden',
            pt: 'Suécia',
            cs: 'Švédsko',
            ro: 'Suedia',
            ru: 'Швеция',
        },
        rootIddCode: '+46',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^SE\\d{12}$',
        taxIdExample: 'SE123456789012',
    },
    SG: {
        i18nNames: {
            en: 'Singapore',
            it: 'Singapore',
            es: 'Singapur',
            de: 'Singapur',
            pl: 'Singapur',
            fr: 'Singapour',
            nl: 'Singapore',
            pt: 'Singapura',
            cs: 'Singapur',
            ro: 'Singapore',
            ru: 'Сингапур',
        },
        rootIddCode: '+65',
        //quando verrà mappata aggiungere taxIdMandatory: false, in quanto non obbligatorio
    },
    SH: {
        i18nNames: {
            en: 'Saint Helena, Ascension and Tristan da Cunha',
            es: 'Santa Elena',
            it: "Sant'Elena, Ascensione e Tristan da Cunha",
            de: 'Sankt Helena, Ascension und Tristan da Cunha',
            pl: 'Wyspa Świętej Heleny, Wniebowstąpienia i Tristan da Cunha',
            fr: 'Sainte-Hélène, Ascension et Tristan da Cunha',
            nl: 'Sint-Helena, Ascension en Tristan da Cunha',
            pt: 'Santa Helena, Ascensão e Tristão da Cunha',
            cs: 'Svatá Helena, Ascension a Tristan da Cunha',
            ro: 'Sfânta Elena, Ascension și Tristan da Cunha',
            ru: 'Остров Святой Елены, Вознесения и Тристан-да-Кунья',
        },
        rootIddCode: '+247',
    },
    SI: {
        i18nNames: {
            en: 'Slovenia',
            it: 'Slovenia',
            es: 'Eslovenia',
            de: 'Slowenien',
            pl: 'Słowenia',
            fr: 'Slovénie',
            nl: 'Slovenië',
            pt: 'Eslovênia',
            cs: 'Slovinsko',
            ro: 'Slovenia',
            ru: 'Словения',
        },
        rootIddCode: '+386',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^SI\\d{8}$',
        taxIdExample: 'SI12345678',
    },
    SJ: {
        i18nNames: {
            en: 'Svalbard and Jan Mayen',
            es: 'Svalbard y Jan Mayen',
            it: 'Svalbard e Jan Mayen',
            de: 'Svalbard und Jan Mayen',
            pl: 'Svalbard i Jan Mayen',
            fr: 'Svalbard et Jan Mayen',
            nl: 'Spitsbergen en Jan Mayen',
            pt: 'Svalbard e Jan Mayen',
            cs: 'Špicberky a Jan Mayen',
            ro: 'Svalbard și Jan Mayen',
            ru: 'Шпицберген и Ян-Майен',
        },
        rootIddCode: '+4779',
    },
    SK: {
        i18nNames: {
            en: 'Slovakia',
            it: 'Slovacchia',
            es: 'Eslovaquia',
            de: 'Slowakei',
            pl: 'Słowacja',
            fr: 'Slovaquie',
            nl: 'Slowakije',
            pt: 'Eslováquia',
            cs: 'Slovensko',
            ro: 'Slovacia',
            ru: 'Словакия',
        },
        rootIddCode: '+421',
        blocMemberships: ['EU'],
        taxIdMandatory: true,
        taxIdPattern: '^SK\\d{10}$',
        taxIdExample: 'SK1234567890',
    },
    SL: {
        i18nNames: {
            en: 'Sierra Leone',
            it: 'Sierra Leone',
            es: 'Sierra Leona',
            de: 'Sierra Leone',
            pl: 'Sierra Leone',
            fr: 'Sierra Leone',
            nl: 'Sierra Leone',
            pt: 'Serra Leoa',
            cs: 'Sierra Leone',
            ro: 'Sierra Leone',
            ru: 'Сьерра-Леоне',
        },
        rootIddCode: '+232',
    },
    SM: {
        i18nNames: {
            en: 'San Marino',
            it: 'San Marino',
            es: 'San Marino',
            de: 'San Marino',
            pl: 'San Marino',
            fr: 'Saint-Marin',
            nl: 'San Marino',
            pt: 'San Marino',
            cs: 'San Marino',
            ro: 'San Marino',
            ru: 'Сан-Марино',
        },
        rootIddCode: '+378',
        taxIdMandatory: true,
        taxIdPattern: '^SM\\d{5}$',
        taxIdExample: 'SM12345',
    },
    SN: {
        i18nNames: {
            en: 'Senegal',
            it: 'Senegal',
            es: 'Senegal',
            de: 'Senegal',
            pl: 'Senegal',
            fr: 'Sénégal',
            nl: 'Senegal',
            pt: 'Senegal',
            cs: 'Senegal',
            ro: 'Senegal',
            ru: 'Сенегал',
        },
        rootIddCode: '+221',
    },
    SO: {
        i18nNames: {
            en: 'Somalia',
            it: 'Somalia',
            es: 'Somalia',
            de: 'Somalia',
            pl: 'Somalia',
            fr: 'Somalie',
            nl: 'Somalië',
            pt: 'Somália',
            cs: 'Somálsko',
            ro: 'Somalia',
            ru: 'Сомали',
        },
        rootIddCode: '+252',
    },
    SR: {
        i18nNames: {
            en: 'Suriname',
            it: 'Suriname',
            es: 'Surinam',
            de: 'Suriname',
            pl: 'Surinam',
            fr: 'Suriname',
            nl: 'Suriname',
            pt: 'Suriname',
            cs: 'Surinam',
            ro: 'Surinam',
            ru: 'Суринам',
        },
        rootIddCode: '+597',
    },
    SS: {
        i18nNames: {
            en: 'South Sudan',
            it: 'Sudan del sud',
            es: 'Sudán del Sur',
            de: 'Südsudan',
            pl: 'Sudan Południowy',
            fr: 'Soudan du Sud',
            nl: 'Zuid-Soedan',
            pt: 'Sudão do Sul',
            cs: 'Jižní Súdán',
            ro: 'Sudanul de Sud',
            ru: 'Южный Судан',
        },
        rootIddCode: '+211',
    },
    ST: {
        i18nNames: {
            en: 'São Tomé and Príncipe',
            es: 'Santo Tomé y Príncipe',
            it: 'São Tomé e Príncipe',
            de: 'São Tomé und Príncipe',
            pl: 'Wyspy Świętego Tomasza i Książęca',
            fr: 'Sao Tomé-et-Principe',
            nl: 'Sao Tomé en Principe',
            pt: 'São Tomé e Príncipe',
            cs: 'Svatý Tomáš a Princův ostrov',
            ro: 'São Tomé și Príncipe',
            ru: 'Сан-Томе и Принсипи',
        },
        rootIddCode: '+239',
    },
    SV: {
        i18nNames: {
            en: 'El Salvador',
            it: 'El Salvador',
            es: 'El Salvador',
            de: 'El Salvador',
            pl: 'Salwador',
            fr: 'Le Salvador',
            nl: 'El Salvador',
            pt: 'El Salvador',
            cs: 'Salvador',
            ro: 'El Salvador',
            ru: 'Сальвадор',
        },
        rootIddCode: '+503',
    },
    SX: {
        i18nNames: {
            en: 'Sint Maarten',
            it: 'Sint Maarten',
            es: 'San Martín (parte neerlandesa)',
            de: 'Sint Maarten',
            pl: 'Sint Maarten',
            fr: 'Saint-Martin (partie néerlandaise)',
            nl: 'Sint Maarten',
            pt: 'Sint Maarten',
            cs: 'Svatý Martin (nizozemská část)',
            ro: 'Sint Maarten',
            ru: 'Синт-Мартен',
        },
        rootIddCode: '+1721',
    },
    SY: {
        i18nNames: {
            en: 'Syria',
            it: 'Siria',
            es: 'Siria',
            de: 'Сирия',
            pl: 'Syria',
            fr: 'Syrie',
            nl: 'Syrië',
            pt: 'Síria',
            cs: 'Sýrie',
            ro: 'Siria',
            ru: 'Сирия',
        },
        rootIddCode: '+963',
    },
    SZ: {
        i18nNames: {
            en: 'Eswatini',
            it: 'Swaziland',
            es: 'Suazilandia',
            de: 'Eswatini',
            pl: 'Eswatini',
            fr: 'Eswatini',
            nl: 'Eswatini',
            pt: 'Essuatíni',
            cs: 'Eswatini',
            ro: 'Eswatini',
            ru: 'Эсватини',
        },
        rootIddCode: '+268',
    },
    TC: {
        i18nNames: {
            en: 'Turks and Caicos Islands',
            es: 'Islas Turcas y Caicos',
            it: 'Isole Turks e Caicos',
            de: 'Turks- und Caicosinseln',
            pl: 'Turks i Caicos',
            fr: 'Îles Turques-et-Caïques',
            nl: 'Turks- en Caicoseilanden',
            pt: 'Ilhas Turcas e Caicos',
            cs: 'Ostrovy Turks a Caicos',
            ro: 'Insulele Turks și Caicos',
            ru: 'Теркс и Кайкос',
        },
        rootIddCode: '+1649',
    },
    TD: {
        i18nNames: {
            en: 'Chad',
            it: 'Ciad',
            es: 'Chad',
            de: 'Tschad',
            pl: 'Czad',
            fr: 'Tchad',
            nl: 'Tsjaad',
            pt: 'Chade',
            cs: 'Čад',
            ro: 'Ciad',
            ru: 'Чад',
        },
        rootIddCode: '+235',
    },
    TF: {
        i18nNames: {
            en: 'French Southern and Antarctic Lands',
            es: 'Territorios Australes Franceses',
            it: 'Territori Francesi del Sud',
            de: 'Französische Süd- und Antarktisgebiete',
            pl: 'Francuskie Terytoria Południowe i Antarktyczne',
            fr: 'Terres australes et antarctiques françaises',
            nl: 'Franse Zuidelijke en Antarctische Gebieden',
            pt: 'Terras Austrais e Antárticas Francesas',
            cs: 'Francouzská jižní a antarktická území',
            ro: 'Teritoriile Australe și Antarctice Franceze',
            ru: 'Французские южные и антарктические территории',
        },
        rootIddCode: '+262',
    },
    TG: {
        i18nNames: {
            en: 'Togo',
            it: 'Togo',
            es: 'Togo',
            de: 'Togo',
            pl: 'Togo',
            fr: 'Togo',
            nl: 'Togo',
            pt: 'Togo',
            cs: 'Togo',
            ro: 'Togo',
            ru: 'Того',
        },
        rootIddCode: '+228',
    },
    TH: {
        i18nNames: {
            en: 'Thailand',
            it: 'Tailandia',
            es: 'Tailandia',
            de: 'Thailand',
            pl: 'Tajlandia',
            fr: 'Thaïlande',
            nl: 'Thailand',
            pt: 'Tailândia',
            cs: 'Thajsko',
            ro: 'Thailanda',
            ru: 'Таиланд',
        },
        rootIddCode: '+66',
    },
    TJ: {
        i18nNames: {
            en: 'Tajikistan',
            it: 'Tagikistan',
            es: 'Tayikistán',
            de: 'Tadschikistan',
            pl: 'Tadżykistan',
            fr: 'Tadjikistan',
            nl: 'Tadzjikistan',
            pt: 'Tajiquistão',
            cs: 'Tádžikistán',
            ro: 'Tadjikistan',
            ru: 'Таджикистан',
        },
        rootIddCode: '+992',
    },
    TK: {
        i18nNames: {
            en: 'Tokelau',
            it: 'Isole Tokelau',
            es: 'Tokelau',
            de: 'Tokelau',
            pl: 'Tokelau',
            fr: 'Tokelau',
            nl: 'Tokelau',
            pt: 'Tokelau',
            cs: 'Tokelau',
            ro: 'Tokelau',
            ru: 'Токелау',
        },
        rootIddCode: '+690',
    },
    TL: {
        i18nNames: {
            en: 'Timor-Leste',
            it: 'Timor Est',
            es: 'Timor-Leste',
            de: 'Osttimor',
            pl: 'Timor Wschodni',
            fr: 'Timor oriental',
            nl: 'Oost-Timor',
            pt: 'Timor-Leste',
            cs: 'Východní Timor',
            ro: 'Timorul de Est',
            ru: 'Восточный Тимор',
        },
        rootIddCode: '+670',
    },
    TM: {
        i18nNames: {
            en: 'Turkmenistan',
            it: 'Turkmenistan',
            es: 'Turkmenistán',
            de: 'Turkmenistan',
            pl: 'Turkmenistan',
            fr: 'Turkménistan',
            nl: 'Turkmenistan',
            pt: 'Turquemenistão',
            cs: 'Turkmenistán',
            ro: 'Turkmenistan',
            ru: 'Туркменистан',
        },
        rootIddCode: '+993',
    },
    TN: {
        i18nNames: {
            en: 'Tunisia',
            it: 'Tunisia',
            es: 'Túnez',
            de: 'Tunesien',
            pl: 'Tunezja',
            fr: 'Tunisie',
            nl: 'Tunesië',
            pt: 'Tunísia',
            cs: 'Tunisko',
            ro: 'Tunisia',
            ru: 'Тунис',
        },
        rootIddCode: '+216',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{8}$',
        taxIdExample: '12345678',
    },
    TO: {
        i18nNames: {
            en: 'Tonga',
            it: 'Tonga',
            es: 'Tonga',
            de: 'Tonga',
            pl: 'Tonga',
            fr: 'Tonga',
            nl: 'Tonga',
            pt: 'Tonga',
            cs: 'Tonga',
            ro: 'Tonga',
            ru: 'Тонга',
        },
        rootIddCode: '+676',
    },
    TR: {
        i18nNames: {
            en: 'Turkey',
            it: 'Turchia',
            es: 'Turquía',
            de: 'Türkei',
            pl: 'Turcja',
            fr: 'Turquie',
            nl: 'Turkije',
            pt: 'Turquia',
            cs: 'Turecko',
            ro: 'Turcia',
            ru: 'Турция',
        },
        rootIddCode: '+90',
    },
    TT: {
        i18nNames: {
            en: 'Trinidad and Tobago',
            es: 'Trinidad y Tobago',
            it: 'Trinidad e Tobago',
            de: 'Trinidad und Tobago',
            pl: 'Trynidad i Tobago',
            fr: 'Trinité-et-Tobago',
            nl: 'Trinidad en Tobago',
            pt: 'Trinidad e Tobago',
            cs: 'Trinidad a Tobago',
            ro: 'Trinidad și Tobago',
            ru: 'Тринидад и Тобаго',
        },
        rootIddCode: '+1868',
    },
    TV: {
        i18nNames: {
            en: 'Tuvalu',
            it: 'Tuvalu',
            es: 'Tuvalu',
            de: 'Tuvalu',
            pl: 'Tuvalu',
            fr: 'Tuvalu',
            nl: 'Tuvalu',
            pt: 'Tuvalu',
            cs: 'Tuvalu',
            ro: 'Tuvalu',
            ru: 'Тувалу',
        },
        rootIddCode: '+688',
    },
    TW: {
        i18nNames: {
            en: 'Taiwan',
            it: 'Taiwan',
            es: 'Taiwán',
            de: 'Taiwan',
            pl: 'Tajwan',
            fr: 'Taïwan',
            nl: 'Taiwan',
            pt: 'Taiwan',
            cs: 'Tchaj-wan',
            ro: 'Taiwan',
            ru: 'Тайвань',
        },
        rootIddCode: '+886',
    },
    TZ: {
        i18nNames: {
            en: 'Tanzania',
            it: 'Tanzania',
            es: 'Tanzania',
            de: 'Tansania',
            pl: 'Tanzania',
            fr: 'Tanzanie',
            nl: 'Tanzania',
            pt: 'Tanzânia',
            cs: 'Tanzanie',
            ro: 'Tanzania',
            ru: 'Танзания',
        },
        rootIddCode: '+255',
    },
    UA: {
        i18nNames: {
            en: 'Ukraine',
            it: 'Ucraina',
            es: 'Ucrania',
            de: 'Ukraine',
            pl: 'Ukraina',
            fr: 'Ukraine',
            nl: 'Oekraïne',
            pt: 'Ucrânia',
            cs: 'Ukrajina',
            ro: 'Ucraina',
            ru: 'Украина',
        },
        rootIddCode: '+380',
        taxIdMandatory: true,
        taxIdPattern: '^(\\d{8}|\\d{12})$',
        taxIdExample: '12345678',
    },
    UG: {
        i18nNames: {
            en: 'Uganda',
            it: 'Uganda',
            es: 'Uganda',
            de: 'Uganda',
            pl: 'Uganda',
            fr: 'Ouganda',
            nl: 'Oeganda',
            pt: 'Uganda',
            cs: 'Uganda',
            ro: 'Uganda',
            ru: 'Уганда',
        },
        rootIddCode: '+256',
    },
    UM: {
        i18nNames: {
            en: 'United States Minor Outlying Islands',
            es: 'Islas menores alejadas de los Estados Unidos',
            it: "Isole minori esterne degli Stati Uniti d'America",
            de: 'Kleinere Inselbesitzungen der Vereinigten Staaten',
            pl: 'Dalekie Wyspy Mniejsze Stanów Zjednoczonych',
            fr: 'Îles mineures éloignées des États-Unis',
            nl: 'Kleine afgelegen eilanden van de Verenigde Staten',
            pt: 'Ilhas Menores Distantes dos EUA',
            cs: 'Menší odlehlé ostrovy USA',
            ro: 'Insulele S.U.A. din Oceanul Pacific',
            ru: 'Внешние малые острова США',
        },
        rootIddCode: '+268',
    },
    US: {
        i18nNames: {
            en: 'United States',
            it: "Stati Uniti d'America",
            es: 'Estados Unidos',
            de: 'Vereinigte Staaten',
            pl: 'Stany Zjednoczone',
            fr: 'États-Unis',
            nl: 'Verenigde Staten',
            pt: 'Estados Unidos',
            cs: 'Spojené státy americké',
            ro: 'Statele Unite ale Americii',
            ru: 'Соединённые Штаты',
        },
        rootIddCode: '+1989',
        //quando verrà mappata aggiungere taxIdMandatory: false, in quanto non obbligatorio
    },
    UY: {
        i18nNames: {
            en: 'Uruguay',
            it: 'Uruguay',
            es: 'Uruguay',
            de: 'Uruguay',
            pl: 'Urugwaj',
            fr: 'Uruguay',
            nl: 'Uruguay',
            pt: 'Uruguai',
            cs: 'Uruguay',
            ro: 'Uruguay',
            ru: 'Уругвай',
        },
        rootIddCode: '+598',
    },
    UZ: {
        i18nNames: {
            en: 'Uzbekistan',
            it: 'Uzbekistan',
            es: 'Uzbekistán',
            de: 'Usbekistan',
            pl: 'Uzbekistan',
            fr: 'Ouzbékistan',
            nl: 'Oezbekistan',
            pt: 'Uzbequistão',
            cs: 'Uzbekistán',
            ro: 'Uzbekistan',
            ru: 'Узбекистан',
        },
        rootIddCode: '+998',
    },
    VA: {
        i18nNames: {
            en: 'Vatican City',
            it: 'Città del Vaticano',
            es: 'Ciudad del Vaticano',
            de: 'Vatikanstadt',
            pl: 'Watykan',
            fr: 'Cité du Vatican',
            nl: 'Vaticaanstad',
            pt: 'Cidade do Vaticano',
            cs: 'Vatikán',
            ro: 'Vatican',
            ru: 'Ватикан',
        },
        rootIddCode: '+379',
    },
    VC: {
        i18nNames: {
            en: 'Saint Vincent and the Grenadines',
            es: 'San Vicente y las Granadinas',
            it: 'Saint Vincent e Grenadine',
            de: 'St. Vincent und die Grenadinen',
            pl: 'Saint Vincent i Grenadyny',
            fr: 'Saint-Vincent-et-les-Grenadines',
            nl: 'Saint Vincent en de Grenadines',
            pt: 'São Vicente e Granadinas',
            cs: 'Svatý Vincenc a Grenadiny',
            ro: 'Sfântul Vincent și Grenadine',
            ru: 'Сент-Винсент и Гренадины',
        },
        rootIddCode: '+1784',
    },
    VE: {
        i18nNames: {
            en: 'Venezuela',
            it: 'Venezuela',
            es: 'Venezuela',
            de: 'Venezuela',
            pl: 'Wenezuela',
            fr: 'Venezuela',
            nl: 'Venezuela',
            pt: 'Venezuela',
            cs: 'Venezuela',
            ro: 'Venezuela',
            ru: 'Венесуэла',
        },
        rootIddCode: '+58',
    },
    VG: {
        i18nNames: {
            en: 'British Virgin Islands',
            es: 'Islas Vírgenes Británicas',
            it: 'Isole Vergini Britanniche',
            de: 'Britische Jungferninseln',
            pl: 'Brytyjskie Wyspy Dziewicze',
            fr: 'Îles Vierges britanniques',
            nl: 'Britse Maagdeneilanden',
            pt: 'Ilhas Virgens Britânicas',
            cs: 'Britské Panenské ostrovy',
            ro: 'Insulele Virgine Britanice',
            ru: 'Британские Виргинские острова',
        },
        rootIddCode: '+1284',
    },
    VI: {
        i18nNames: {
            en: 'United States Virgin Islands',
            es: 'Islas Vírgenes de los Estados Unidos',
            it: 'Isole Vergini americane',
            de: 'Amerikanische Jungferninseln',
            pl: 'Wyspy Dziewицze Stanów Зjednoczonych',
            fr: 'Îles Vierges des États-Unis',
            nl: 'Amerikaanse Maagdeneilanden',
            pt: 'Ilhas Virgens dos Estados Unidos',
            cs: 'Americké Panenské остроvy',
            ro: 'Insulele Virgine Americane',
            ru: 'Виргинские острова США',
        },
        rootIddCode: '+1340',
    },
    VN: {
        i18nNames: {
            en: 'Vietnam',
            it: 'Vietnam',
            es: 'Vietnam',
            de: 'Vietnam',
            pl: 'Wietnam',
            fr: 'Vietnam',
            nl: 'Vietnam',
            pt: 'Vietnã',
            cs: 'Vietnam',
            ro: 'Vietnam',
            ru: 'Вьетнам',
        },
        rootIddCode: '+84',
    },
    VU: {
        i18nNames: {
            en: 'Vanuatu',
            it: 'Vanuatu',
            es: 'Vanuatu',
            de: 'Vanuatu',
            pl: 'Vanuatu',
            fr: 'Vanuatu',
            nl: 'Vanuatu',
            pt: 'Vanuatu',
            cs: 'Vanuatu',
            ro: 'Vanuatu',
            ru: 'Вануату',
        },
        rootIddCode: '+678',
    },
    WF: {
        i18nNames: {
            en: 'Wallis and Futuna',
            es: 'Wallis y Futuna',
            it: 'Wallis e Futuna',
            de: 'Wallis und Futuna',
            pl: 'Wallis i Futuna',
            fr: 'Wallis-et-Futuna',
            nl: 'Wallis en Futuna',
            pt: 'Wallis e Futuna',
            cs: 'Wallis a Futuna',
            ro: 'Wallis și Futuna',
            ru: 'Уоллис и Футуна',
        },
        rootIddCode: '+681',
    },
    WS: {
        i18nNames: {
            en: 'Samoa',
            it: 'Samoa',
            es: 'Samoa',
            de: 'Samoa',
            pl: 'Samoa',
            fr: 'Samoa',
            nl: 'Samoa',
            pt: 'Samoa',
            cs: 'Samoa',
            ro: 'Samoa',
            ru: 'Самоа',
        },
        rootIddCode: '+685',
    },
    XK: {
        i18nNames: {
            en: 'Kosovo',
            it: 'Kosovo',
            es: 'Kosovo',
            de: 'Kosovo',
            pl: 'Kosowo',
            fr: 'Kosovo',
            nl: 'Kosovo',
            pt: 'Kosovo',
            cs: 'Kosovo',
            ro: 'Kosovo',
            ru: 'Косово',
        },
        rootIddCode: '+383',
        taxIdMandatory: true,
        taxIdPattern: '^\\d{9}$',
        taxIdExample: '123456789',
    },
    YE: {
        i18nNames: {
            en: 'Yemen',
            it: 'Yemen',
            es: 'Yemen',
            de: 'Jemen',
            pl: 'Jemen',
            fr: 'Yémen',
            nl: 'Jemen',
            pt: 'Iémen',
            cs: 'Jemen',
            ro: 'Yemen',
            ru: 'Йемен',
        },
        rootIddCode: '+967',
    },
    YT: {
        i18nNames: {
            en: 'Mayotte',
            it: 'Mayotte',
            es: 'Mayotte',
            de: 'Mayotte',
            pl: 'Majotta',
            fr: 'Mayotte',
            nl: 'Mayotte',
            pt: 'Mayotte',
            cs: 'Mayotte',
            ro: 'Mayotte',
            ru: 'Майотта',
        },
        rootIddCode: '+262',
    },
    ZA: {
        i18nNames: {
            en: 'South Africa',
            it: 'Sud Africa',
            es: 'Sudáfrica',
            de: 'Südafrika',
            pl: 'Republika Południowej Afryki',
            fr: 'Afrique du Sud',
            nl: 'Zuid-Afrika',
            pt: 'África do Sul',
            cs: 'Jihoafrická republika',
            ro: 'Africa de Sud',
            ru: 'Южная Африка',
        },
        rootIddCode: '+27',
    },
    ZM: {
        i18nNames: {
            en: 'Zambia',
            it: 'Zambia',
            es: 'Zambia',
            de: 'Sambia',
            pl: 'Zambia',
            fr: 'Zambie',
            nl: 'Zambia',
            pt: 'Zâmbia',
            cs: 'Zambie',
            ro: 'Zambia',
            ru: 'Замбия',
        },
        rootIddCode: '+260',
    },
    ZW: {
        i18nNames: {
            en: 'Zimbabwe',
            it: 'Zimbabwe',
            es: 'Zimbabue',
            de: 'Simbabwe',
            pl: 'Zimbabwe',
            fr: 'Zimbabwe',
            nl: 'Zimbabwe',
            pt: 'Zimbábue',
            cs: 'Zimbabwe',
            ro: 'Zimbabwe',
            ru: 'Зимбабве',
        },
        rootIddCode: '+263',
    },
} as const satisfies Record<CountryISO2, CountryConfig>;
